import dashboardOverview from "../../assets/seo-screenshots/dashboard-overview.png";
import ideaGenerator from "../../assets/seo-screenshots/idea-generator.png";
import myDivisionLeaderboard from "../../assets/seo-screenshots/my-division-leaderboard.png";
import myIndividualLeaderboard from "../../assets/seo-screenshots/my-individual-leaderboard.png";
import myOrgLeaderboard from "../../assets/seo-screenshots/my-org-leaderboard.png";
import myTeamLeaderboard from "../../assets/seo-screenshots/my-team-leaderboard.png";
import myTeamView from "../../assets/seo-screenshots/my-team-view.png";
import myTools from "../../assets/seo-screenshots/my-tools.png";
import notificationsCenter from "../../assets/seo-screenshots/notifications-center.png";
import projectPortfolio from "../../assets/seo-screenshots/project-portfolio.png";
import reportingExecutiveSummary from "../../assets/seo-screenshots/reporting-executive-summary.png";

import innovationImg from "../../assets/innovation.png";
import ideaScorecardsImg from "../../assets/idea_scorecards.png";
import executiveDashboardsImg from "../../assets/executive_dashboards.png";
import riskAssessmentImg from "../../assets/risk_assessment.png";
import instantBusinessCasesImg from "../../assets/instant_business_cases.png";
import advancedForecastingImg from "../../assets/advanced_forecasting.png";
import automatedTaskManagementImg from "../../assets/automated_task_management.png";
import clearPlanAlignmentImg from "../../assets/clear_plan_alignment.png";
import dynamicStrategyImg from "../../assets/dynamic_strategy_deployment.png";
import goalTrackingImg from "../../assets/goal_tracking.png";
import integratedWorkflowsImg from "../../assets/integrated_workflows.png";
import performanceMetricsImg from "../../assets/performance_metrics.png";
import sentimentAnalysisImg from "../../assets/sentiment_analysis.png";
import smartNotificationsImg from "../../assets/smart_notifications.png";
import unifiedCommunicationImg from "../../assets/unified_communication.png";
import aiEnhancementImg from "../../assets/ai_enhancement.png";
import effortlessIdeaCaptureImg from "../../assets/effortless_idea_capture.png";
import customerVoicesImg from "../../assets/customer_voices.png";
import aiWhiteboardImg from "../../assets/ai_powered_whiteboard.png";
import engagingCultureImg from "../../assets/engaging_culture.png";

const SITE_URL = "https://compelion.com";

const CATEGORY_LABELS = {
  commercial: "Commercial Page",
  comparison: "Comparison Page",
  resource: "Template Page",
  solution: "Use-Case Page",
  guide: "Guide Page",
};

const SECTION_LABELS = {
  commercial: {
    capabilities: "What teams can do with Compelion",
    workflow: "How the operating model works",
    fit: "Teams that benefit most",
    proof: "Why buyers use this page in evaluation",
    related: "Related Compelion pages",
  },
  comparison: {
    capabilities: "Where Compelion is a better fit",
    workflow: "How the evaluation process changes",
    fit: "Who this comparison matters to",
    proof: "How to evaluate alternatives more rigorously",
    related: "Next pages to compare",
  },
  resource: {
    capabilities: "What the resource helps you do",
    workflow: "How to use it in practice",
    fit: "Teams that get the most value",
    proof: "What this resource is designed to support",
    related: "Related templates and product pages",
  },
  solution: {
    capabilities: "What this team can achieve with Compelion",
    workflow: "How this operating model works",
    fit: "Who this page is written for",
    proof: "Why this workflow matters",
    related: "Related solution and product pages",
  },
  guide: {
    capabilities: "What this guide covers",
    workflow: "Recommended approach",
    fit: "Who should read this",
    proof: "What strong teams do differently",
    related: "Continue exploring",
  },
};

const defaultCta = {
  title: "See how this works in your organisation",
  body: "Book a working session to map Compelion to your innovation, transformation, delivery, or governance process.",
  primaryLabel: "Book a consultation",
  primaryTo: "/schedule-consultation",
  secondaryLabel: "View pricing",
  secondaryTo: "/pricing",
};

const ideaCta = {
  title: "See the evaluation workflow in action",
  body: "If your team is trying to improve how ideas are assessed, start with Compelion's guided evaluation flow and map it to your intake criteria.",
  primaryLabel: "Try the evaluation flow",
  primaryTo: "/idea-evaluation",
  secondaryLabel: "Book a consultation",
  secondaryTo: "/schedule-consultation",
};

/* Feature illustrations — used as hero primaryImage */
const heroIllustrations = {
  innovation: {
    src: innovationImg,
    alt: "AI-powered innovation management",
    caption: "Bring ideas to life with intelligent innovation workflows.",
  },
  ideaScorecards: {
    src: ideaScorecardsImg,
    alt: "Structured idea scorecard evaluation",
    caption: "Score and evaluate ideas with structured assessment criteria.",
  },
  executiveDashboards: {
    src: executiveDashboardsImg,
    alt: "Executive dashboard with portfolio visibility",
    caption: "Executive-level dashboards for portfolio-wide visibility.",
  },
  riskAssessment: {
    src: riskAssessmentImg,
    alt: "Project risk assessment and escalation",
    caption: "Identify, assess, and escalate project risks proactively.",
  },
  instantBusinessCases: {
    src: instantBusinessCasesImg,
    alt: "AI-generated business case creation",
    caption: "Generate structured business cases faster with AI assistance.",
  },
  advancedForecasting: {
    src: advancedForecastingImg,
    alt: "Advanced forecasting and delivery assurance",
    caption: "Forecast delivery outcomes with data-driven assurance.",
  },
  automatedTaskManagement: {
    src: automatedTaskManagementImg,
    alt: "Automated task management workflows",
    caption: "Automate task workflows and keep delivery on track.",
  },
  clearPlanAlignment: {
    src: clearPlanAlignmentImg,
    alt: "Clear plan alignment and coordination",
    caption: "Align plans, teams, and strategic objectives in one view.",
  },
  dynamicStrategy: {
    src: dynamicStrategyImg,
    alt: "Dynamic strategy deployment",
    caption: "Deploy strategy dynamically with real-time execution context.",
  },
  goalTracking: {
    src: goalTrackingImg,
    alt: "Goal tracking and prioritisation",
    caption: "Track goals, priorities, and progress across initiatives.",
  },
  integratedWorkflows: {
    src: integratedWorkflowsImg,
    alt: "Integrated project workflows",
    caption: "Connect workflows across teams, tools, and delivery stages.",
  },
  performanceMetrics: {
    src: performanceMetricsImg,
    alt: "Performance metrics and analytics",
    caption: "Measure performance with actionable metrics and insights.",
  },
  sentimentAnalysis: {
    src: sentimentAnalysisImg,
    alt: "Sentiment analysis and assurance signals",
    caption: "Analyse project sentiment for early warning signals.",
  },
  smartNotifications: {
    src: smartNotificationsImg,
    alt: "Smart notifications and decision workflows",
    caption: "Stay informed with intelligent notification workflows.",
  },
  unifiedCommunication: {
    src: unifiedCommunicationImg,
    alt: "Unified team communication",
    caption: "Connect teams with unified communication and collaboration.",
  },
  aiEnhancement: {
    src: aiEnhancementImg,
    alt: "AI enhancement and automation",
    caption: "Enhance decision-making with AI-powered automation.",
  },
  effortlessIdeaCapture: {
    src: effortlessIdeaCaptureImg,
    alt: "Effortless idea capture and intake",
    caption: "Capture ideas effortlessly with guided intake workflows.",
  },
  customerVoices: {
    src: customerVoicesImg,
    alt: "Customer and stakeholder feedback",
    caption: "Incorporate stakeholder feedback into delivery decisions.",
  },
  aiWhiteboard: {
    src: aiWhiteboardImg,
    alt: "AI-powered whiteboard collaboration",
    caption: "Collaborate visually with AI-powered whiteboard tools.",
  },
  engagingCulture: {
    src: engagingCultureImg,
    alt: "Innovation culture and engagement",
    caption: "Build an engaging innovation culture across your organisation.",
  },
};

/* Product screenshots — used as secondaryImage */
const heroScreenshots = {
  dashboard: {
    src: dashboardOverview,
    alt: "Compelion dashboard overview showing tasks, projects, and calendar",
    caption: "Live product view: dashboard visibility inside Compelion.",
  },
  ideas: {
    src: ideaGenerator,
    alt: "Compelion AI assistant guiding idea report generation",
    caption: "Live product view: AI-assisted idea generation and structuring.",
  },
  portfolio: {
    src: projectPortfolio,
    alt: "Compelion project portfolio view showing initiatives by stage",
    caption: "Live product view: stage-based project and portfolio tracking.",
  },
  notifications: {
    src: notificationsCenter,
    alt: "Compelion notifications center showing decisions and assurance items",
    caption:
      "Live product view: decisions, escalation, and assurance workflows.",
  },
  executiveSummary: {
    src: reportingExecutiveSummary,
    alt: "Compelion executive summary report view with structured initiative reporting",
    caption:
      "Live product view: executive reporting and summary output inside Compelion.",
  },
  teamView: {
    src: myTeamView,
    alt: "Compelion team workspace showing collaborative project and delivery context",
    caption:
      "Live product view: team-level delivery and collaboration visibility.",
  },
  orgLeaderboard: {
    src: myOrgLeaderboard,
    alt: "Compelion organisation leaderboard showing ranked performance and delivery insight",
    caption:
      "Live product view: organisation-level ranking and oversight signals.",
  },
  divisionLeaderboard: {
    src: myDivisionLeaderboard,
    alt: "Compelion division leaderboard showing ranked performance across delivery groups",
    caption:
      "Live product view: division-level performance and prioritisation insight.",
  },
  teamLeaderboard: {
    src: myTeamLeaderboard,
    alt: "Compelion team leaderboard showing ranked progress and contribution insight",
    caption:
      "Live product view: team ranking and progress visibility in Compelion.",
  },
  individualLeaderboard: {
    src: myIndividualLeaderboard,
    alt: "Compelion individual leaderboard showing ranked contribution and performance insight",
    caption:
      "Live product view: individual contribution and performance visibility.",
  },
  tools: {
    src: myTools,
    alt: "Compelion tools view showing available workflow and productivity modules",
    caption:
      "Live product view: the tools and workflow modules available in Compelion.",
  },
};

const related = (to, title, description) => ({ to, title, description });

const comparisonRows = (rows) => rows.map((row) => ({ ...row }));

const createBreadcrumbs = (path, title) => {
  const breadcrumbs = [{ label: "Home", to: "/" }];

  if (path.startsWith("/compare/")) {
    breadcrumbs.push({ label: "Compare", to: "/compare/clickup-alternative" });
  } else if (path.startsWith("/templates/")) {
    breadcrumbs.push({
      label: "Templates",
      to: "/templates/idea-evaluation-template",
    });
  } else if (path.startsWith("/solutions/")) {
    breadcrumbs.push({ label: "Solutions", to: "/solutions/innovation-teams" });
  } else if (path.startsWith("/learn/")) {
    breadcrumbs.push({
      label: "Learn",
      to: "/learn/how-to-evaluate-business-ideas",
    });
  }

  breadcrumbs.push({ label: title, to: path });
  return breadcrumbs;
};

const buildStructuredData = (page) => {
  const canonicalUrl = `${SITE_URL}${page.path}`;

  const primarySchema =
    page.type === "guide"
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: page.seoTitle,
          description: page.seoDescription,
          mainEntityOfPage: canonicalUrl,
          author: {
            "@type": "Organization",
            name: "Compelion",
          },
          publisher: {
            "@type": "Organization",
            name: "Compelion",
            url: SITE_URL,
          },
        }
      : page.type === "resource"
        ? {
            "@context": "https://schema.org",
            "@type": "TechArticle",
            headline: page.seoTitle,
            description: page.seoDescription,
            mainEntityOfPage: canonicalUrl,
            author: {
              "@type": "Organization",
              name: "Compelion",
            },
          }
        : {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: page.seoTitle,
            description: page.seoDescription,
            url: canonicalUrl,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP",
            },
          };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: page.breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.label,
      item: `${SITE_URL}${crumb.to}`,
    })),
  };

  return [primarySchema, breadcrumbSchema];
};

const createPage = ({
  key,
  path,
  type,
  title,
  seoTitle,
  seoDescription,
  intro,
  bullets,
  stats,
  primaryImage,
  secondaryImage,
  capabilities,
  workflow,
  fit,
  faqs,
  relatedLinks,
  proofPoints,
  comparison,
  resourceItems,
  cta = defaultCta,
}) => {
  const breadcrumbs = createBreadcrumbs(path, title);
  return {
    key,
    path,
    type,
    eyebrow: CATEGORY_LABELS[type],
    title,
    seoTitle,
    seoDescription,
    description: seoDescription,
    intro,
    bullets,
    stats,
    primaryImage,
    secondaryImage,
    capabilities,
    workflow,
    fit,
    faqs,
    relatedLinks,
    proofPoints,
    comparison,
    resourceItems,
    cta,
    breadcrumbs,
    sectionLabels: SECTION_LABELS[type],
  };
};

const pages = [
  createPage({
    key: "aiInnovationManagement",
    path: "/ai-innovation-management-software",
    type: "commercial",
    title: "AI Innovation Management Software That Moves Ideas Into Delivery",
    seoTitle: "AI Innovation Management Software | Compelion",
    seoDescription:
      "Compelion helps innovation, transformation, and strategy teams evaluate ideas, prioritise initiatives, generate business cases, and connect innovation work to delivery execution.",
    intro:
      "Replace scattered submissions, subjective prioritisation, and disconnected delivery updates with a single operating system for innovation work.",
    bullets: [
      "Capture ideas with structure instead of inbox chaos",
      "Use AI to accelerate assessment and business-case creation",
      "Connect innovation decisions to project execution and oversight",
    ],
    stats: [
      {
        value: "1 platform",
        label: "for idea intake, prioritisation, and delivery",
      },
      {
        value: "AI guided",
        label: "evaluation and report-generation workflows",
      },
      {
        value: "Portfolio ready",
        label: "views for projects, status, and oversight",
      },
    ],
    primaryImage: heroIllustrations.innovation,
    secondaryImage: heroScreenshots.teamView,
    capabilities: [
      {
        title: "AI-assisted idea intake",
        text: "Collect opportunities with enough context to evaluate them consistently instead of relying on one-line submissions with missing evidence.",
      },
      {
        title: "Structured prioritisation",
        text: "Score and compare initiatives against impact, fit, urgency, and delivery signals so decisions become auditable.",
      },
      {
        title: "Business-case acceleration",
        text: "Generate reports, summaries, and supporting rationale faster so promising ideas do not stall before review.",
      },
      {
        title: "Execution visibility",
        text: "Move approved work into projects, tasks, notifications, and portfolio views without losing context.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Collect",
        text: "Capture ideas from teams with a repeatable intake flow.",
      },
      {
        step: "02",
        title: "Evaluate",
        text: "Use AI and structured criteria to build a defensible view of value, risk, and effort.",
      },
      {
        step: "03",
        title: "Prioritise",
        text: "Compare initiatives at portfolio level and decide what moves forward.",
      },
      {
        step: "04",
        title: "Deliver",
        text: "Track execution, risks, and progress in the same platform.",
      },
    ],
    fit: [
      "Innovation teams standardising idea-to-delivery workflows",
      "Transformation offices managing competing strategic initiatives",
      "PMOs that need better upstream business-case quality",
    ],
    proofPoints: [
      "This page combines the upstream innovation and downstream delivery story, which is where generic PM tools are usually weakest.",
      "The screenshots show real application surfaces rather than abstract diagrams, which makes the offer more concrete for evaluators.",
      "Compelion is strongest when the buyer needs evaluation, prioritisation, reporting, and oversight in one system.",
    ],
    faqs: [
      {
        question:
          "What makes Compelion different from generic project management tools?",
        answer:
          "Compelion starts earlier in the lifecycle. It is built for idea evaluation, prioritisation, business-case development, and governance before and during delivery, not just downstream task execution.",
      },
      {
        question:
          "Can Compelion support both innovation and project oversight?",
        answer:
          "Yes. The platform is designed to connect idea generation and evaluation with project tracking, portfolio visibility, and escalation management.",
      },
      {
        question: "Who is this page for?",
        answer:
          "It is best suited to organisations running structured innovation, transformation, improvement, or strategic initiative portfolios.",
      },
    ],
    relatedLinks: [
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "See how Compelion helps teams assess ideas with more structure and speed.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Explore how prioritised ideas roll into portfolio visibility and governance.",
      ),
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "See how Compelion supports delivery assurance after prioritisation decisions.",
      ),
    ],
  }),
  createPage({
    key: "ideaEvaluationSoftware",
    path: "/idea-evaluation-software",
    type: "commercial",
    title: "Idea Evaluation Software for Faster, More Defensible Decisions",
    seoTitle: "Idea Evaluation Software | Compelion",
    seoDescription:
      "Evaluate ideas faster with Compelion's AI-assisted workflows for structured assessment, report generation, and decision-ready outputs.",
    intro:
      "When every idea looks promising on paper, evaluation quality becomes the bottleneck. Compelion gives you a repeatable way to judge ideas before time and budget are committed.",
    bullets: [
      "Standardise how ideas are captured and reviewed",
      "Reduce subjective decision-making with clear criteria",
      "Turn early concepts into usable reports and next steps",
    ],
    stats: [
      { value: "Repeatable", label: "intake and evaluation process" },
      { value: "AI supported", label: "questioning and report generation" },
      { value: "Decision ready", label: "outputs for sponsors and reviewers" },
    ],
    primaryImage: heroIllustrations.ideaScorecards,
    secondaryImage: heroScreenshots.ideas,
    capabilities: [
      {
        title: "Guided evaluation workflow",
        text: "Lead contributors through the questions and context needed for strong early-stage assessment.",
      },
      {
        title: "AI-generated reports",
        text: "Translate raw inputs into more complete idea summaries, supporting detail, and structured outputs.",
      },
      {
        title: "Better review readiness",
        text: "Give decision-makers a clearer basis for challenge, approval, or iteration.",
      },
      {
        title: "Cleaner handoff to delivery",
        text: "Preserve the logic behind the idea so approved work is easier to plan and govern.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Submit",
        text: "Capture the problem, concept, and expected value in a guided flow.",
      },
      {
        step: "02",
        title: "Interrogate",
        text: "Use AI prompts to expose missing assumptions, dependencies, and risks.",
      },
      {
        step: "03",
        title: "Generate",
        text: "Create a structured report that stakeholders can actually review.",
      },
      {
        step: "04",
        title: "Decide",
        text: "Route stronger ideas toward prioritisation, sponsorship, and planning.",
      },
    ],
    fit: [
      "Teams overwhelmed by large numbers of uneven idea submissions",
      "Innovation leads who need more consistent scoring and evidence",
      "Organisations that want faster movement from concept to review",
    ],
    proofPoints: [
      "The product supports more than submission; it structures the hard questions needed before governance review.",
      "AI-generated outputs help reviewers move from raw notes toward a usable business-case discussion.",
      "This page is useful when the current intake process creates too much low-quality demand noise.",
    ],
    faqs: [
      {
        question: "Is Compelion just an idea submission form?",
        answer:
          "No. It supports the deeper evaluation work after submission, including AI-guided refinement, report generation, and preparation for governance review.",
      },
      {
        question: "Can the output be used in business-case discussions?",
        answer:
          "Yes. The platform is designed to help teams move from rough concepts toward structured rationale and clearer next-step decisions.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer:
          "Innovation teams, transformation functions, continuous improvement teams, and PMOs dealing with upstream demand quality all benefit.",
      },
    ],
    relatedLinks: [
      related(
        "/ai-innovation-management-software",
        "AI innovation management software",
        "See the broader platform that surrounds the idea evaluation workflow.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "See how approved ideas roll into prioritised portfolios and tracked initiatives.",
      ),
      related(
        "/templates/idea-evaluation-template",
        "Idea evaluation template",
        "Give teams a practical resource to standardise the scoring conversation.",
      ),
    ],
    cta: ideaCta,
  }),
  createPage({
    key: "innovationPortfolioManagement",
    path: "/innovation-portfolio-management",
    type: "commercial",
    title: "Innovation Portfolio Management Software With Delivery Context",
    seoTitle: "Innovation Portfolio Management Software | Compelion",
    seoDescription:
      "Track prioritised initiatives, project stages, and governance signals with Compelion's innovation portfolio management software.",
    intro:
      "Portfolio management breaks down when prioritisation, delivery, and governance are handled in separate systems. Compelion keeps the operating picture connected.",
    bullets: [
      "Compare initiatives with a shared decision framework",
      "Track project stages, progress, and delivery load",
      "Keep governance grounded in live execution context",
    ],
    stats: [
      {
        value: "Stage based",
        label: "visibility across planned, active, and completed work",
      },
      {
        value: "Cross-functional",
        label: "portfolio view for transformation and PMO teams",
      },
      {
        value: "Governance ready",
        label: "context for escalations and oversight",
      },
    ],
    primaryImage: heroIllustrations.executiveDashboards,
    secondaryImage: heroScreenshots.portfolio,
    capabilities: [
      {
        title: "Portfolio-wide prioritisation",
        text: "Stack-rank or review initiatives with visibility into what is already in flight and where capacity is constrained.",
      },
      {
        title: "Stage-based project views",
        text: "See planned, in-progress, completed, archived, and parked work in one operating picture.",
      },
      {
        title: "Governance alignment",
        text: "Bring decision-makers closer to current execution status instead of relying on disconnected updates.",
      },
      {
        title: "Better transition from strategy to execution",
        text: "Keep the reasoning behind prioritised work visible as teams move into delivery and tracking.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Assemble",
        text: "Bring candidate initiatives and existing projects into one portfolio view.",
      },
      {
        step: "02",
        title: "Prioritise",
        text: "Use structured criteria to decide what deserves sponsorship and sequencing.",
      },
      {
        step: "03",
        title: "Monitor",
        text: "Track stage movement, progress, and emerging delivery signals across the portfolio.",
      },
      {
        step: "04",
        title: "Govern",
        text: "Support steering and escalation decisions with more current evidence.",
      },
    ],
    fit: [
      "Transformation offices balancing multiple initiative streams",
      "PMOs needing clearer portfolio visibility and prioritisation discipline",
      "Strategy teams tracking which bets are progressing and which are stalling",
    ],
    proofPoints: [
      "This view is strongest when leaders need one operating picture across planned, active, completed, and parked work.",
      "The portfolio surface supports prioritisation conversations with more context than a static status slide.",
      "Compelion is a better fit than standalone PM reporting when governance decisions start upstream of delivery execution.",
    ],
    faqs: [
      {
        question: "Does Compelion replace generic project portfolio tools?",
        answer:
          "Compelion is strongest where idea evaluation, prioritisation, governance, and delivery context all matter together. It is not just a list of projects; it is the operating layer between opportunity and execution.",
      },
      {
        question: "Can leaders see work by stage?",
        answer:
          "Yes. The platform supports stage-based visibility across planned, in-progress, completed, archived, and parked initiatives.",
      },
      {
        question: "Is this relevant only to large enterprises?",
        answer:
          "No. It is useful anywhere multiple initiatives compete for attention and leadership needs better prioritisation and oversight discipline.",
      },
    ],
    relatedLinks: [
      related(
        "/ai-innovation-management-software",
        "AI innovation management software",
        "Return to the broader category page for the full Compelion operating model.",
      ),
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "See how the portfolio starts with stronger idea assessment upstream.",
      ),
      related(
        "/learn/innovation-prioritisation-framework",
        "Innovation prioritisation framework",
        "Read the guide that supports portfolio prioritisation decisions.",
      ),
    ],
  }),
  createPage({
    key: "healthcareInnovationManagement",
    path: "/healthcare-innovation-management-software",
    type: "commercial",
    title:
      "Healthcare Innovation Management Software for Improvement and Transformation Teams",
    seoTitle: "Healthcare Innovation Management Software | Compelion",
    seoDescription:
      "Compelion helps healthcare innovation and quality improvement teams evaluate ideas, build business cases, prioritise initiatives, and track delivery with stronger governance.",
    intro:
      "Healthcare teams face heavy governance, constrained capacity, and strong evidence requirements. Compelion helps move improvement ideas forward with more structure and traceability.",
    bullets: [
      "Support quality improvement and innovation pipelines",
      "Build stronger cases around patient benefit, operational impact, and risk",
      "Track initiatives beyond approval into delivery and oversight",
    ],
    stats: [
      {
        value: "Evidence aware",
        label: "for governance and review-heavy environments",
      },
      {
        value: "Cross-functional",
        label: "for operational, clinical, and transformation stakeholders",
      },
      {
        value: "Delivery linked",
        label: "from approved idea to monitored initiative",
      },
    ],
    primaryImage: heroIllustrations.customerVoices,
    secondaryImage: heroScreenshots.teamView,
    capabilities: [
      {
        title: "Improvement idea evaluation",
        text: "Structure submissions so teams can assess patient impact, operational benefit, feasibility, and implementation dependencies.",
      },
      {
        title: "Business-case support",
        text: "Turn early proposals into clearer reports and rationale for boards, review groups, and sponsors.",
      },
      {
        title: "Portfolio oversight",
        text: "See which initiatives are planned, active, delayed, parked, or completed across the improvement portfolio.",
      },
      {
        title: "Governance continuity",
        text: "Keep the evidence and context behind each project visible as work progresses.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Capture",
        text: "Collect improvement ideas from frontline teams and service leaders.",
      },
      {
        step: "02",
        title: "Assess",
        text: "Evaluate value, evidence strength, operational impact, and delivery requirements.",
      },
      {
        step: "03",
        title: "Approve",
        text: "Prepare stronger cases for innovation, QI, or transformation governance.",
      },
      {
        step: "04",
        title: "Track",
        text: "Monitor approved initiatives through delivery and escalation workflows.",
      },
    ],
    fit: [
      "Healthcare innovation teams",
      "Quality improvement and transformation offices",
      "Operational leaders coordinating cross-service change programmes",
    ],
    proofPoints: [
      "The platform supports the stronger evidence trail that healthcare initiatives usually require.",
      "This page is especially relevant when improvement ideas are currently tracked in disconnected forms, decks, and spreadsheets.",
      "Healthcare buyers usually care about governance continuity as much as idea generation, which is where Compelion is differentiated.",
    ],
    faqs: [
      {
        question: "Is this only for large health systems?",
        answer:
          "No. The same model works for trusts, providers, improvement collaboratives, and smaller cross-functional healthcare teams that need better structure around innovation and improvement work.",
      },
      {
        question:
          "Can Compelion support quality improvement as well as innovation?",
        answer:
          "Yes. It can support structured assessment, prioritisation, reporting, and tracking for both innovation and quality improvement initiatives.",
      },
      {
        question: "Why not use a project tool alone?",
        answer:
          "Healthcare innovation work often fails before delivery because intake, governance, and evidence quality are weak. Compelion addresses that upstream problem as well as downstream tracking.",
      },
    ],
    relatedLinks: [
      related(
        "/learn/healthcare-innovation-governance",
        "Healthcare innovation governance",
        "Read the guide that supports this healthcare positioning page.",
      ),
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "See the upstream workflow that strengthens healthcare proposal quality.",
      ),
      related(
        "/solutions/healthcare",
        "Healthcare solution page",
        "Explore the broader healthcare solution framing for Compelion.",
      ),
    ],
  }),
  createPage({
    key: "projectRiskEscalationManagement",
    path: "/project-risk-and-escalation-management",
    type: "commercial",
    title:
      "Project Risk and Escalation Management Software for Delivery Assurance",
    seoTitle: "Project Risk and Escalation Management Software | Compelion",
    seoDescription:
      "Compelion helps teams surface delivery risks, manage escalation workflows, and keep leadership aligned with clearer decisions, assurance, and accountability.",
    intro:
      "Delivery issues are not just tracking problems. They are governance problems. Compelion helps teams move from buried signals to visible escalation and assurance workflows.",
    bullets: [
      "Centralise decisions, approvals, and assurance signals",
      "Make overdue and high-priority items visible sooner",
      "Support leadership action with stronger delivery context",
    ],
    stats: [
      {
        value: "Decision focused",
        label: "workflows for approvals and escalation",
      },
      {
        value: "High visibility",
        label: "on overdue and high-priority notifications",
      },
      {
        value: "Governance linked",
        label: "to active delivery and project context",
      },
    ],
    primaryImage: heroIllustrations.riskAssessment,
    secondaryImage: heroScreenshots.notifications,
    capabilities: [
      {
        title: "Escalation workflows",
        text: "Surface high-priority issues, approvals, and decisions in one place instead of letting them disappear in inboxes and meetings.",
      },
      {
        title: "Assurance visibility",
        text: "Track assurance and delivery signals so teams can intervene before problems spread.",
      },
      {
        title: "Project-linked context",
        text: "Keep escalation items anchored to the project or initiative they affect.",
      },
      {
        title: "Stronger accountability",
        text: "Support clearer follow-up, ownership, and action at the right leadership level.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Detect",
        text: "Collect decisions, risk signals, and assurance issues from active work.",
      },
      {
        step: "02",
        text: "Flag high-priority and overdue items that need action.",
      },
      {
        step: "03",
        title: "Escalate",
        text: "Route issues through the right governance channel with the right context.",
      },
      {
        step: "04",
        title: "Resolve",
        text: "Track actions and outcomes instead of losing the escalation trail.",
      },
    ],
    fit: [
      "PMOs and delivery assurance teams",
      "Transformation leaders dealing with high-risk programmes",
      "Teams that need better decision traceability and escalation hygiene",
    ],
    proofPoints: [
      "The notification surface makes approvals, tasks, and assurance items visible in one operational hub.",
      "This is useful when escalation currently happens through fragmented email chains and slide decks.",
      "Compelion is a better fit when governance rigor matters as much as raw task management.",
    ],
    faqs: [
      {
        question: "Does this replace a RAID log?",
        answer:
          "It can support the operating workflow around risks and escalations, but its advantage is that it links those issues to active projects, decisions, and notifications rather than leaving them in a static log alone.",
      },
      {
        question: "Can leaders use this for decision-making?",
        answer:
          "Yes. The goal is to make critical items, status, and urgency visible enough that leadership can intervene earlier and with better context.",
      },
      {
        question: "Is this only for large programmes?",
        answer:
          "No. It is useful wherever risk signals and escalation ownership are currently weak or inconsistent.",
      },
    ],
    relatedLinks: [
      related(
        "/learn/project-escalation-workflow",
        "Project escalation workflow",
        "Read the supporting guide for this delivery assurance page.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Connect delivery risks back to the portfolio decisions around them.",
      ),
      related(
        "/delivery-assurance-analytics",
        "Delivery assurance analytics",
        "See the analytics view that supports delivery governance.",
      ),
    ],
  }),
  createPage({
    key: "aiBusinessCaseGenerator",
    path: "/ai-business-case-generator",
    type: "commercial",
    title: "AI Business Case Generator for Stronger Initiative Justification",
    seoTitle: "AI Business Case Generator | Compelion",
    seoDescription:
      "Compelion helps teams turn rough initiative ideas into more structured business cases with AI-assisted questioning, report generation, and clearer delivery rationale.",
    intro:
      "Many initiatives fail in governance because the case for change is weak, incomplete, or inconsistent. Compelion accelerates the hard work of structuring the business case properly.",
    bullets: [
      "Move faster from concept note to structured business case",
      "Improve consistency across teams and submissions",
      "Connect business-case logic to downstream delivery planning",
    ],
    stats: [
      { value: "AI assisted", label: "for report and rationale generation" },
      { value: "Structured", label: "outputs for sponsors and reviewers" },
      { value: "Linked", label: "to idea evaluation and execution workflows" },
    ],
    primaryImage: heroIllustrations.instantBusinessCases,
    secondaryImage: heroScreenshots.executiveSummary,
    capabilities: [
      {
        title: "Guided case building",
        text: "Use prompts and structured capture to improve the quality of the case, not just the formatting of the document.",
      },
      {
        title: "AI-generated drafting",
        text: "Accelerate summaries, rationale, and supporting sections that normally slow teams down.",
      },
      {
        title: "More consistent submissions",
        text: "Create a common standard for sponsors and governance teams reviewing multiple proposals.",
      },
      {
        title: "Smoother transition to delivery",
        text: "Keep the business-case logic close to the work it eventually becomes.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Capture",
        text: "Collect the problem, proposed approach, value, and delivery assumptions.",
      },
      {
        step: "02",
        title: "Challenge",
        text: "Use guided questions to expose gaps in evidence, risk, and feasibility.",
      },
      {
        step: "03",
        title: "Generate",
        text: "Create a clearer draft business case with more decision-ready structure.",
      },
      {
        step: "04",
        title: "Refine",
        text: "Use the output in governance and planning conversations.",
      },
    ],
    fit: [
      "Innovation teams building cases for investment",
      "Transformation offices reviewing multiple proposals",
      "PMOs and sponsors that need more consistent submission quality",
    ],
    proofPoints: [
      "The same workflow improves both speed and consistency of case preparation.",
      "This page is most relevant when teams already know what they want to propose but struggle to justify it clearly.",
      "Compelion is more useful than generic generative writing tools because the business case stays linked to evaluation and delivery workflows.",
    ],
    faqs: [
      {
        question: "Does the tool write the full business case for me?",
        answer:
          "It helps structure and accelerate the process, but teams still need to supply the right evidence, assumptions, and sponsor input.",
      },
      {
        question: "Can it support cost-benefit thinking?",
        answer:
          "Yes. The workflow is designed to support clearer discussion of value, operational impact, and delivery implications.",
      },
      {
        question: "When is this page most relevant?",
        answer:
          "When teams need to improve the quality and consistency of business-case development without slowing decisions down.",
      },
    ],
    relatedLinks: [
      related(
        "/templates/business-case-template",
        "Business case template",
        "Use the template version for teams that need a practical starting point.",
      ),
      related(
        "/learn/how-to-write-a-business-case",
        "How to write a business case",
        "Read the guide that supports the same buyer intent from an educational angle.",
      ),
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "Strengthen ideas before they become business cases.",
      ),
    ],
  }),
  createPage({
    key: "clickupAlternative",
    path: "/compare/clickup-alternative",
    type: "comparison",
    title:
      "Compelion vs ClickUp Alternative for Innovation and Transformation Work",
    seoTitle: "ClickUp Alternative for Innovation Management | Compelion",
    seoDescription:
      "Compare Compelion with ClickUp when your team needs stronger idea evaluation, business-case generation, portfolio governance, and delivery assurance workflows.",
    intro:
      "ClickUp is broad and flexible. Compelion is narrower and stronger where the operating problem starts before task execution, especially around evaluation, governance, and strategic prioritisation.",
    bullets: [
      "Use Compelion when idea quality and business-case rigor matter",
      "Use ClickUp when generic work management flexibility is the main need",
      "Compare tools based on governance workflow, not just task features",
    ],
    stats: [
      { value: "Better upstream fit", label: "for idea-to-decision workflows" },
      {
        value: "Governance stronger",
        label: "for prioritisation and escalation",
      },
      {
        value: "Category specific",
        label: "than general-purpose work management",
      },
    ],
    primaryImage: heroIllustrations.automatedTaskManagement,
    secondaryImage: heroScreenshots.teamView,
    capabilities: [
      {
        title: "Idea evaluation is first-class",
        text: "Compelion is designed for structured idea assessment and report generation rather than assuming all work is already approved and ready for tasks.",
      },
      {
        title: "Business-case support",
        text: "Compelion helps turn early proposals into clearer decision material, which is not the core ClickUp proposition.",
      },
      {
        title: "Portfolio and governance context",
        text: "Compelion is stronger when the buyer needs prioritisation, assurance, and escalation linked to initiative oversight.",
      },
      {
        title: "Less generic, more opinionated",
        text: "That makes Compelion a better fit for organisations that want a defined operating model rather than a blank canvas.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Assess the real problem",
        text: "Decide whether your bottleneck is work execution or upstream idea and governance quality.",
      },
      {
        step: "02",
        title: "Map the buyer journey",
        text: "Compare how each product supports intake, evaluation, approval, and delivery.",
      },
      {
        step: "03",
        title: "Test the workflows",
        text: "Use live product views to check fit against your current process.",
      },
      {
        step: "04",
        title: "Choose based on fit",
        text: "Pick the system that matches the job to be done, not the most general feature list.",
      },
    ],
    fit: [
      "Transformation offices choosing between work management and governance tooling",
      "Innovation teams that have outgrown generic task-centric systems",
      "PMOs needing stronger upstream demand quality and oversight",
    ],
    proofPoints: [
      "The comparison should start with where the operating problem actually begins, not which UI looks more flexible.",
      "Compelion is stronger where work needs to be justified, prioritised, and governed before it is merely tracked.",
      "Generic tools often need significant process design layered on top to reach the same use case.",
    ],
    comparison: {
      headers: ["Evaluation area", "Compelion", "ClickUp"],
      rows: comparisonRows([
        {
          topic: "Idea evaluation",
          left: "Built for guided evaluation and report generation",
          right: "Usually handled through custom forms and docs",
        },
        {
          topic: "Business-case support",
          left: "Part of the operating model",
          right: "Not a primary workflow",
        },
        {
          topic: "Portfolio governance",
          left: "Supports prioritisation and oversight context",
          right: "Broad project and task management with flexible setup",
        },
        {
          topic: "Best fit",
          left: "Innovation, transformation, PMO governance",
          right: "General work management across many teams",
        },
      ]),
    },
    faqs: [
      {
        question: "Is Compelion a full replacement for ClickUp?",
        answer:
          "Not in every situation. If your main need is broad generic work management, ClickUp may still fit. Compelion is the better fit when idea evaluation, prioritisation, governance, and assurance are the real operating problem.",
      },
      {
        question: "Why compare these tools at all?",
        answer:
          "Because many teams try to solve innovation governance problems with task tools alone. This page helps buyers assess whether that is actually the right category fit.",
      },
      {
        question: "Who should read this page?",
        answer:
          "Leaders choosing between broad PM tooling and a more specialised innovation and delivery governance platform.",
      },
    ],
    relatedLinks: [
      related(
        "/compare/asana-alternative",
        "Asana alternative",
        "Compare Compelion against another broad work-management option.",
      ),
      related(
        "/ai-innovation-management-software",
        "AI innovation management software",
        "Return to the core commercial category page.",
      ),
      related(
        "/schedule-consultation",
        "Schedule a consultation",
        "Book a discussion if the category-fit question is central to your evaluation.",
      ),
    ],
  }),
  createPage({
    key: "asanaAlternative",
    path: "/compare/asana-alternative",
    type: "comparison",
    title:
      "Compelion vs Asana Alternative for Governance-Heavy Initiative Work",
    seoTitle:
      "Asana Alternative for Innovation and Governance Work | Compelion",
    seoDescription:
      "Compare Compelion with Asana when your team needs stronger initiative evaluation, business-case development, portfolio governance, and escalation visibility.",
    intro:
      "Asana is strong for broad team coordination. Compelion is stronger when the buyer needs structured initiative intake, prioritisation, governance, and delivery assurance around strategic work.",
    bullets: [
      "Compelion is stronger upstream of delivery execution",
      "Asana is broader for generic work coordination",
      "The right choice depends on whether the bottleneck is governance or tasking",
    ],
    stats: [
      {
        value: "Upstream fit",
        label: "for evaluation and sponsorship workflows",
      },
      {
        value: "Transformation fit",
        label: "for innovation and transformation programmes",
      },
      {
        value: "Governance aware",
        label: "for escalation and assurance use cases",
      },
    ],
    primaryImage: heroIllustrations.integratedWorkflows,
    secondaryImage: heroScreenshots.teamLeaderboard,
    capabilities: [
      {
        title: "Initiative screening",
        text: "Compelion is designed to help teams decide which initiatives deserve approval before delivery work begins.",
      },
      {
        title: "Business-case and report support",
        text: "The platform is more opinionated around preparing stronger governance material.",
      },
      {
        title: "Escalation and assurance",
        text: "Compelion better supports teams that need to surface high-priority risks and decisions.",
      },
      {
        title: "Portfolio narrative",
        text: "The product keeps the link between why work was prioritised and how it is progressing.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Define the evaluation scope",
        text: "Clarify whether you are choosing a task tool or an innovation-governance operating layer.",
      },
      {
        step: "02",
        title: "Review buyer use cases",
        text: "Check whether initiative intake, prioritisation, and sponsorship are core needs.",
      },
      {
        step: "03",
        title: "Assess governance depth",
        text: "Compare how each tool handles assurance, escalation, and decision support.",
      },
      {
        step: "04",
        title: "Choose on fit",
        text: "Select the category that best reflects your real operating gap.",
      },
    ],
    fit: [
      "Strategy and transformation teams",
      "PMOs running governance-heavy delivery portfolios",
      "Innovation teams that need more than project assignment and updates",
    ],
    proofPoints: [
      "This comparison matters most when the buyer needs a more structured operating model around initiative quality and governance.",
      "Compelion will not win on generic breadth; it wins when the work starts before Asana-style execution management begins.",
      "Teams often make a category mistake here, which is why this page is commercially useful.",
    ],
    comparison: {
      headers: ["Evaluation area", "Compelion", "Asana"],
      rows: comparisonRows([
        {
          topic: "Idea intake and evaluation",
          left: "Purpose-built and guided",
          right: "Usually custom-built with forms and tasks",
        },
        {
          topic: "Business-case generation",
          left: "Built into the workflow story",
          right: "Not a primary product use case",
        },
        {
          topic: "Escalation visibility",
          left: "Supports decisions and assurance context",
          right: "Better known for coordination and project planning",
        },
        {
          topic: "Best fit",
          left: "Innovation, transformation, governance-heavy work",
          right: "Cross-functional work management",
        },
      ]),
    },
    faqs: [
      {
        question: "Is Compelion trying to be Asana?",
        answer:
          "No. The value proposition is different. Compelion is more specialised around innovation, evaluation, business-case quality, governance, and assurance.",
      },
      {
        question: "Could an Asana customer still use Compelion?",
        answer:
          "Yes. Some organisations may use a governance or intake layer like Compelion alongside broader execution tools when those tools alone do not solve the upstream problem.",
      },
      {
        question: "What buyer should choose Compelion here?",
        answer:
          "A buyer whose biggest challenge is deciding, governing, and escalating the right work rather than simply assigning and updating it.",
      },
    ],
    relatedLinks: [
      related(
        "/compare/motion-alternative",
        "Motion alternative",
        "Continue through the comparison cluster.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Return to the category page behind this comparison.",
      ),
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "See the assurance workflow page that supports this comparison.",
      ),
    ],
  }),
  createPage({
    key: "motionAlternative",
    path: "/compare/motion-alternative",
    type: "comparison",
    title:
      "Compelion vs Motion Alternative for Strategic Initiative and Portfolio Work",
    seoTitle: "Motion Alternative for Strategic Initiative Work | Compelion",
    seoDescription:
      "Compare Compelion with Motion when your team needs initiative evaluation, portfolio visibility, governance, and escalation support beyond scheduling and personal productivity.",
    intro:
      "Motion is often evaluated for planning and scheduling efficiency. Compelion is relevant when the buyer's problem is strategic initiative quality, prioritisation, governance, and delivery assurance.",
    bullets: [
      "Motion is stronger for calendar and planning optimisation",
      "Compelion is stronger for initiative evaluation and governance",
      "The category gap here is large, so intent needs to be clarified early",
    ],
    stats: [
      {
        value: "Strategic fit",
        label: "for initiative and portfolio governance",
      },
      {
        value: "Category distance",
        label: "between productivity and governance software",
      },
      {
        value: "Useful comparison",
        label: "when buyers are exploring alternatives broadly",
      },
    ],
    primaryImage: heroIllustrations.dynamicStrategy,
    secondaryImage: heroScreenshots.individualLeaderboard,
    capabilities: [
      {
        title: "More than scheduling",
        text: "Compelion focuses on the quality, justification, and governance of strategic work rather than calendar optimisation.",
      },
      {
        title: "Portfolio-level oversight",
        text: "The platform is designed for multi-initiative decision-making and monitoring.",
      },
      {
        title: "Escalation and assurance",
        text: "Compelion is stronger when governance and delivery risk visibility matter.",
      },
      {
        title: "Business-case support",
        text: "It helps teams build the case for change, not just organise the work already agreed.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Clarify the job to be done",
        text: "Decide whether the problem is personal productivity or initiative governance.",
      },
      {
        step: "02",
        title: "Map evaluation scope",
        text: "Assess whether business-case quality and prioritisation are central requirements.",
      },
      {
        step: "03",
        title: "Compare governance depth",
        text: "Check how each product supports portfolio visibility and escalation.",
      },
      {
        step: "04",
        title: "Choose the right category",
        text: "Avoid selecting productivity software for governance-heavy transformation work.",
      },
    ],
    fit: [
      "Leaders comparing tools across categories",
      "Teams that initially searched for planning alternatives but actually need governance software",
      "Buyers looking for a more strategic initiative platform",
    ],
    proofPoints: [
      "This page works because many comparison searches are category-blended and not perfectly informed.",
      "Compelion is not a calendar optimisation tool, so the positioning must stay clear and honest.",
      "The page is commercially useful when it educates the buyer into the right category rather than pretending the products do the same job.",
    ],
    comparison: {
      headers: ["Evaluation area", "Compelion", "Motion"],
      rows: comparisonRows([
        {
          topic: "Core use case",
          left: "Innovation, prioritisation, governance, delivery assurance",
          right: "Scheduling, planning, and productivity",
        },
        {
          topic: "Business-case support",
          left: "Yes",
          right: "No core workflow",
        },
        {
          topic: "Portfolio oversight",
          left: "Yes, stage and initiative oriented",
          right: "Not the main product story",
        },
        {
          topic: "Best fit",
          left: "Strategic initiative teams",
          right: "Individuals and teams optimising time and task flow",
        },
      ]),
    },
    faqs: [
      {
        question: "Are these tools direct competitors?",
        answer:
          "Not exactly. This page exists because buyer intent can be broad, but the real category fit is different.",
      },
      {
        question: "Who should choose Compelion in this comparison?",
        answer:
          "A buyer responsible for initiative quality, governance, portfolio decisions, or delivery assurance rather than day-to-day scheduling alone.",
      },
      {
        question: "Why publish this page if the tools are different?",
        answer:
          "Because comparison searches often surface category confusion. A useful comparison page helps the buyer understand what type of software they actually need.",
      },
    ],
    relatedLinks: [
      related(
        "/compare/clickup-alternative",
        "ClickUp alternative",
        "Return to a comparison against a broader work-management platform.",
      ),
      related(
        "/ai-innovation-management-software",
        "AI innovation management software",
        "Explore the main category page behind the comparison cluster.",
      ),
      related(
        "/learn/innovation-prioritisation-framework",
        "Innovation prioritisation framework",
        "Read the guide that supports strategic initiative selection.",
      ),
    ],
  }),
  createPage({
    key: "ideaEvaluationTemplate",
    path: "/templates/idea-evaluation-template",
    type: "resource",
    title: "Idea Evaluation Template for Structured Innovation Review",
    seoTitle: "Idea Evaluation Template | Compelion",
    seoDescription:
      "Use this idea evaluation template to review opportunities with clearer criteria, stronger questions, and more consistent decision-making across teams.",
    intro:
      "A good idea evaluation template forces the right questions early. Use this page to give teams a practical framework for reviewing ideas with more consistency.",
    bullets: [
      "Create a shared scoring and challenge structure",
      "Improve the quality of early-stage innovation review",
      "Use the template as a bridge into a stronger evaluation workflow",
    ],
    stats: [
      { value: "Practical", label: "for real team review sessions" },
      {
        value: "Transferable",
        label: "across innovation and transformation teams",
      },
      {
        value: "Product linked",
        label: "to the Compelion evaluation workflow",
      },
    ],
    primaryImage: heroIllustrations.effortlessIdeaCapture,
    secondaryImage: heroScreenshots.ideas,
    capabilities: [
      {
        title: "Consistency",
        text: "Use a shared set of prompts and evaluation areas so ideas are not judged on charisma alone.",
      },
      {
        title: "Better sponsor conversations",
        text: "Structure the information sponsors need before they decide whether to back the idea.",
      },
      {
        title: "Clearer next steps",
        text: "Make it easier to accept, reject, or refine ideas with a more consistent record.",
      },
      {
        title: "Workflow bridge",
        text: "Move from a lightweight template into a full evaluation and governance platform when needed.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Describe the idea",
        text: "Capture the problem, proposed change, and intended outcome.",
      },
      {
        step: "02",
        title: "Test the case",
        text: "Assess value, fit, feasibility, evidence, and delivery implications.",
      },
      {
        step: "03",
        title: "Score and discuss",
        text: "Review the template as a group rather than relying on single-person judgment.",
      },
      {
        step: "04",
        title: "Route the idea",
        text: "Use the output to decide whether the idea needs refinement, approval, or rejection.",
      },
    ],
    fit: [
      "Innovation teams that need a lightweight starting point",
      "Transformation functions standardising review practices",
      "PMOs looking to improve upstream demand quality",
    ],
    proofPoints: [
      "Templates earn links because they provide immediate practical value, not just product positioning.",
      "This page is most useful when teams are not ready for a full platform rollout but need a better evaluation structure now.",
      "The commercial value comes from tying the template to a stronger underlying workflow and software path.",
    ],
    resourceItems: [
      "Problem statement and opportunity framing",
      "Expected benefits and strategic fit",
      "Evidence, assumptions, and delivery risks",
      "Recommendation and next-step decision",
    ],
    faqs: [
      {
        question: "Who should use this template?",
        answer:
          "Any team that needs a better structure for reviewing ideas before resources are committed.",
      },
      {
        question: "Can this replace software?",
        answer:
          "It can improve the process immediately, but software becomes valuable when you need repeatability, reporting, and governance continuity across many ideas.",
      },
      {
        question: "How should this template be used?",
        answer:
          "Use it as a facilitated review structure, not just a form to be completed and forgotten.",
      },
    ],
    relatedLinks: [
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "Move from the template to the full evaluation workflow.",
      ),
      related(
        "/learn/how-to-evaluate-business-ideas",
        "How to evaluate business ideas",
        "Read the supporting educational guide.",
      ),
      related(
        "/templates/business-case-template",
        "Business case template",
        "Continue to the next resource in the same cluster.",
      ),
    ],
    cta: ideaCta,
  }),
  createPage({
    key: "businessCaseTemplate",
    path: "/templates/business-case-template",
    type: "resource",
    title:
      "Business Case Template for Innovation and Transformation Initiatives",
    seoTitle: "Business Case Template | Compelion",
    seoDescription:
      "Use this business case template to structure initiative rationale, expected value, delivery implications, and decision criteria before governance review.",
    intro:
      "A business case should do more than justify spend. It should make the logic of the initiative clear enough for governance, challenge, and prioritisation.",
    bullets: [
      "Create a clearer case for change",
      "Standardise how teams present initiative rationale",
      "Use the template as a bridge into AI-assisted case generation",
    ],
    stats: [
      {
        value: "Governance ready",
        label: "structure for sponsors and review groups",
      },
      { value: "Reusable", label: "across innovation and transformation work" },
    ],
    primaryImage: heroIllustrations.aiEnhancement,
    secondaryImage: heroScreenshots.executiveSummary,
    capabilities: [
      {
        title: "Clear rationale",
        text: "Explain the problem, opportunity, and expected value in a consistent structure.",
      },
      {
        title: "Delivery realism",
        text: "Capture risks, dependencies, operational impact, and feasibility alongside ambition.",
      },
      {
        title: "Decision support",
        text: "Help sponsors compare options using a more common format across proposals.",
      },
      {
        title: "Stronger follow-through",
        text: "Connect the business case to the work it is intended to justify.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Frame the problem",
        text: "Explain the current state, pressure, or missed opportunity.",
      },
      {
        step: "02",
        title: "Articulate the proposal",
        text: "Define the intervention, expected outcome, and strategic fit.",
      },
      {
        step: "03",
        title: "Assess implications",
        text: "Capture costs, benefits, risks, and delivery dependencies.",
      },
      {
        step: "04",
        title: "Prepare for decision",
        text: "Use the template as the starting point for sponsor review.",
      },
    ],
    fit: [
      "Teams building stronger initiative proposals",
      "PMOs standardising business-case formats",
      "Transformation offices reviewing multiple change requests",
    ],
    proofPoints: [
      "This resource is useful because many teams need immediate practical help before they are ready to adopt software.",
      "The page works best when it teaches the buyer what a strong case should contain rather than acting as a thin download gate.",
      "Commercial relevance stays strong because the same intent leads naturally into AI-assisted case generation.",
    ],
    resourceItems: [
      "Problem, opportunity, and current-state context",
      "Expected benefits and strategic alignment",
      "Cost, risk, and implementation considerations",
      "Recommendation, ownership, and decision criteria",
    ],
    faqs: [
      {
        question: "What makes a strong business case template?",
        answer:
          "It should force clarity on value, strategic fit, delivery realism, and decision implications rather than only document the idea attractively.",
      },
      {
        question: "Should every proposal use the same template?",
        answer:
          "A shared structure helps governance, comparison, and sponsor review, even if some sections vary by initiative type.",
      },
      {
        question: "How does this relate to Compelion software?",
        answer:
          "Compelion helps teams go beyond the static template by structuring, accelerating, and tracking the business-case workflow inside the platform.",
      },
    ],
    relatedLinks: [
      related(
        "/ai-business-case-generator",
        "AI business case generator",
        "See the software page behind this template resource.",
      ),
      related(
        "/learn/how-to-write-a-business-case",
        "How to write a business case",
        "Read the companion guide.",
      ),
      related(
        "/templates/project-prioritisation-matrix",
        "Project prioritisation matrix",
        "Continue to the next practical resource.",
      ),
    ],
  }),
  createPage({
    key: "projectPrioritisationMatrix",
    path: "/templates/project-prioritisation-matrix",
    type: "resource",
    title: "Project Prioritisation Matrix for Strategic Initiative Selection",
    seoTitle: "Project Prioritisation Matrix | Compelion",
    seoDescription:
      "Use this project prioritisation matrix to compare initiatives against impact, urgency, fit, feasibility, and delivery implications before portfolio decisions are made.",
    intro:
      "A prioritisation matrix is only useful if it creates better decisions, not false precision. This page gives teams a practical structure for comparing competing initiatives.",
    bullets: [
      "Create a common prioritisation language across teams",
      "Reduce subjective sponsorship bias",
      "Support stronger portfolio sequencing conversations",
    ],
    stats: [
      {
        value: "Decision friendly",
        label: "for portfolio review conversations",
      },
      { value: "Lightweight", label: "enough for practical team use" },
      {
        value: "Expandable",
        label: "into software-based portfolio governance",
      },
    ],
    primaryImage: heroIllustrations.goalTracking,
    secondaryImage: heroScreenshots.divisionLeaderboard,
    capabilities: [
      {
        title: "Portfolio comparison",
        text: "Compare multiple initiatives against a common set of evaluation dimensions.",
      },
      {
        title: "Bias reduction",
        text: "Make it harder for noise, rank, or urgency theatre to overwhelm better decisions.",
      },
      {
        title: "Sequencing support",
        text: "Use the matrix to discuss timing, capacity, and sponsor focus more clearly.",
      },
      {
        title: "Governance bridge",
        text: "Use the resource as a stepping stone toward live portfolio management software.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Set criteria",
        text: "Agree the dimensions that matter for portfolio decisions.",
      },
      {
        step: "02",
        title: "Score consistently",
        text: "Assess each initiative against the same framework.",
      },
      {
        step: "03",
        title: "Review as a portfolio",
        text: "Look for patterns, conflicts, and over-commitment.",
      },
      {
        step: "04",
        title: "Decide the sequence",
        text: "Use the matrix to inform sponsorship and timing decisions.",
      },
    ],
    fit: [
      "Transformation offices prioritising change demand",
      "PMOs balancing multiple initiatives",
      "Innovation teams comparing bets across a shared portfolio",
    ],
    proofPoints: [
      "This resource attracts links because it is immediately useful during portfolio review work.",
      "The commercial handoff is natural because the same buyer often needs software once the matrix is used repeatedly at scale.",
      "The page works best when it teaches buyers how to avoid false certainty in prioritisation scoring.",
    ],
    resourceItems: [
      "Strategic fit and expected value",
      "Urgency and timing considerations",
      "Feasibility, risk, and delivery complexity",
      "Capacity impact and sponsorship readiness",
    ],
    faqs: [
      {
        question: "What should be in a prioritisation matrix?",
        answer:
          "Use criteria that actually drive investment decisions, such as value, fit, urgency, feasibility, and delivery implications.",
      },
      {
        question: "Can a matrix make prioritisation objective?",
        answer:
          "Not fully. It improves the quality of judgment, but leadership still needs to interpret the results carefully.",
      },
      {
        question: "How does this connect to Compelion?",
        answer:
          "Compelion helps teams operationalise the same prioritisation logic inside a live portfolio and governance workflow.",
      },
    ],
    relatedLinks: [
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "See the software page that sits behind this matrix.",
      ),
      related(
        "/learn/innovation-prioritisation-framework",
        "Innovation prioritisation framework",
        "Read the educational guide that supports this template.",
      ),
      related(
        "/solutions/pmo",
        "PMO solution page",
        "See how PMOs can use Compelion across prioritisation and delivery.",
      ),
    ],
  }),
  createPage({
    key: "solutionInnovationTeams",
    path: "/solutions/innovation-teams",
    type: "solution",
    title: "Compelion for Innovation Teams",
    seoTitle: "Innovation Team Software | Compelion",
    seoDescription:
      "See how Compelion helps innovation teams capture ideas, evaluate opportunities, build stronger cases, prioritise initiatives, and track progress with better governance.",
    intro:
      "Innovation teams often run on enthusiasm, workshops, and spreadsheets until scale exposes the cracks. Compelion helps create a working operating system around the innovation pipeline.",
    bullets: [
      "Structure the innovation pipeline from intake to delivery",
      "Reduce weak submissions and shallow sponsor conversations",
      "Keep prioritised initiatives visible after approval",
    ],
    stats: [
      { value: "Pipeline aware", label: "from idea to execution" },
      {
        value: "Governance ready",
        label: "for sponsor and review discussions",
      },
      {
        value: "Execution linked",
        label: "to active initiatives and oversight",
      },
    ],
    primaryImage: heroIllustrations.clearPlanAlignment,
    secondaryImage: heroScreenshots.teamView,
    capabilities: [
      {
        title: "Capture better ideas",
        text: "Create stronger intake quality by guiding contributors through the right information.",
      },
      {
        title: "Review more consistently",
        text: "Use shared evaluation logic across many proposals rather than ad hoc sponsor preference.",
      },
      {
        title: "Build stronger cases",
        text: "Support business-case quality with AI-assisted structuring and reporting.",
      },
      {
        title: "Track the portfolio",
        text: "Keep approved innovation work visible as it progresses into execution.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Collect ideas",
        text: "Gather opportunities from teams and stakeholders.",
      },
      {
        step: "02",
        title: "Screen and refine",
        text: "Improve submission quality before sponsor review.",
      },
      {
        step: "03",
        title: "Prioritise and sponsor",
        text: "Compare initiatives and move the best ones forward.",
      },
      {
        step: "04",
        title: "Monitor outcomes",
        text: "Track delivery and governance after approval.",
      },
    ],
    fit: [
      "Innovation offices",
      "Strategy and improvement teams running innovation portfolios",
      "Organisations scaling beyond informal innovation processes",
    ],
    proofPoints: [
      "Innovation teams need both creativity support and governance discipline, which is why the positioning is narrower than generic PM software.",
      "This use-case page converts when buyers see their pipeline and sponsor-review pain points reflected accurately.",
      "The best internal links from here are the idea evaluation and portfolio pages.",
    ],
    faqs: [
      {
        question: "What does Compelion solve for innovation teams?",
        answer:
          "It helps create a more structured operating model around idea intake, evaluation, prioritisation, case building, and follow-through.",
      },
      {
        question: "Is this suitable only for large innovation teams?",
        answer:
          "No. The page is relevant to any team trying to bring more structure and repeatability to innovation work.",
      },
      {
        question: "Why not use forms and spreadsheets?",
        answer:
          "They can work briefly, but they usually break down when visibility, governance, and portfolio coordination become more important.",
      },
    ],
    relatedLinks: [
      related(
        "/ai-innovation-management-software",
        "AI innovation management software",
        "Return to the broader category page for innovation buyers.",
      ),
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "See the most relevant product page for this audience.",
      ),
      related(
        "/learn/how-to-evaluate-business-ideas",
        "How to evaluate business ideas",
        "Support the use-case page with educational content.",
      ),
    ],
  }),
  createPage({
    key: "solutionTransformationOffices",
    path: "/solutions/transformation-offices",
    type: "solution",
    title: "Compelion for Transformation Offices",
    seoTitle: "Transformation Office Software | Compelion",
    seoDescription:
      "Compelion helps transformation offices prioritise competing initiatives, improve proposal quality, strengthen governance, and maintain delivery visibility across the portfolio.",
    intro:
      "Transformation offices sit between strategy ambition and delivery reality. Compelion helps them make better prioritisation choices and keep those choices linked to real delivery outcomes.",
    bullets: [
      "Improve the quality of incoming change demand",
      "Support stronger portfolio sequencing decisions",
      "Keep governance linked to current delivery signals",
    ],
    stats: [
      {
        value: "Portfolio aware",
        label: "for transformation and change initiatives",
      },
      {
        value: "Decision focused",
        label: "on prioritisation and sponsor review",
      },
      {
        value: "Execution linked",
        label: "to stage-based delivery visibility",
      },
    ],
    primaryImage: heroIllustrations.advancedForecasting,
    secondaryImage: heroScreenshots.orgLeaderboard,
    capabilities: [
      {
        title: "Demand quality",
        text: "Raise the quality of proposals entering the portfolio so prioritisation meetings are less noisy.",
      },
      {
        title: "Portfolio sequencing",
        text: "Compare initiatives with more structured criteria instead of relying on politics and urgency theatre.",
      },
      {
        title: "Governance continuity",
        text: "Keep the logic of sponsorship decisions visible as work moves into execution.",
      },
      {
        title: "Escalation visibility",
        text: "Support decision-makers with clearer delivery and assurance signals.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Collect demand",
        text: "Bring initiatives into a shared intake and evaluation process.",
      },
      {
        step: "02",
        title: "Prioritise",
        text: "Assess value, urgency, feasibility, and strategic fit.",
      },
      {
        step: "03",
        title: "Govern",
        text: "Make sponsor decisions with better visibility and stronger cases.",
      },
      {
        step: "04",
        title: "Steer delivery",
        text: "Monitor execution, escalations, and portfolio health.",
      },
    ],
    fit: [
      "Enterprise transformation offices",
      "Corporate strategy and change portfolios",
      "Leaders coordinating multiple initiative streams across business units",
    ],
    proofPoints: [
      "Transformation offices usually need stronger upstream demand control as much as downstream status reporting.",
      "This page is relevant when leadership is overwhelmed by too many competing initiatives and weak comparability.",
      "The strongest commercial links from here are into portfolio management and escalation management.",
    ],
    faqs: [
      {
        question: "How does Compelion help a transformation office?",
        answer:
          "It supports initiative intake, prioritisation, governance, and delivery visibility across a multi-initiative portfolio.",
      },
      {
        question: "Does it replace programme management tools?",
        answer:
          "Not always. It is often most valuable as the operating layer around initiative quality, prioritisation, and governance discipline.",
      },
      {
        question: "Why is this different from project reporting software?",
        answer:
          "Because transformation offices need to decide what should be in the portfolio, not just report what is already there.",
      },
    ],
    relatedLinks: [
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "See the core product page most aligned to transformation-office use cases.",
      ),
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "Explore the delivery-assurance page that supports governance.",
      ),
      related(
        "/templates/project-prioritisation-matrix",
        "Project prioritisation matrix",
        "Use the template as a practical resource for transformation teams.",
      ),
    ],
  }),
  createPage({
    key: "solutionPmo",
    path: "/solutions/pmo",
    type: "solution",
    title: "Compelion for PMOs and Portfolio Governance Teams",
    seoTitle: "PMO Project Prioritisation Software | Compelion",
    seoDescription:
      "Compelion helps PMOs improve project intake quality, prioritise initiatives, support governance, and monitor delivery risk with more connected visibility.",
    intro:
      "PMOs often inherit weak initiative proposals and fragmented escalation workflows. Compelion helps create a stronger operating layer between proposal, sponsorship, and delivery oversight.",
    bullets: [
      "Improve upstream project demand quality",
      "Support prioritisation with more consistent criteria",
      "Strengthen governance, assurance, and escalation visibility",
    ],
    stats: [
      {
        value: "PMO aligned",
        label: "around governance, prioritisation, and oversight",
      },
      { value: "Risk aware", label: "for escalation and assurance workflows" },
    ],
    primaryImage: heroIllustrations.smartNotifications,
    secondaryImage: heroScreenshots.orgLeaderboard,
    capabilities: [
      {
        title: "Better intake quality",
        text: "Give PMOs stronger proposals to assess instead of weak requests that are hard to compare.",
      },
      {
        title: "More consistent prioritisation",
        text: "Use common evaluation dimensions across a portfolio of competing initiatives.",
      },
      {
        title: "Governance support",
        text: "Keep sponsor decisions, approvals, and review outputs closer to the work they affect.",
      },
      {
        title: "Delivery risk visibility",
        text: "Bring assurance signals and escalations into a clearer operational flow.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Improve intake",
        text: "Raise the quality of project requests entering the PMO pipeline.",
      },
      {
        step: "02",
        title: "Prioritise the portfolio",
        text: "Compare projects against common criteria and delivery realities.",
      },
      {
        step: "03",
        title: "Govern approvals",
        text: "Support steering and review boards with better material.",
      },
      {
        step: "04",
        title: "Monitor risk",
        text: "Keep assurance and escalation workflows visible during delivery.",
      },
    ],
    fit: [
      "Enterprise PMOs",
      "Portfolio governance functions",
      "Delivery assurance and programme management leaders",
    ],
    proofPoints: [
      "PMOs often need help before projects enter the delivery tracker, which is why this positioning is commercially distinct from generic PM software.",
      "The risk and escalation surface is especially relevant to mature PMO buyers.",
      "This use-case page should funnel authority to prioritisation and escalation product pages.",
    ],
    faqs: [
      {
        question: "How does Compelion help PMOs specifically?",
        answer:
          "It helps PMOs improve intake quality, prioritise more consistently, support governance, and keep delivery risk visible.",
      },
      {
        question: "Is this just another project tracker?",
        answer:
          "No. The differentiation is in upstream evaluation, governance, and connected oversight, not only project execution tracking.",
      },
      {
        question: "When is this page most relevant?",
        answer:
          "When a PMO is dealing with weak project proposals, poor prioritisation hygiene, or fragmented escalation processes.",
      },
    ],
    relatedLinks: [
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "See the strongest adjacent product page for PMO buyers.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Return to the core portfolio page.",
      ),
      related(
        "/learn/project-escalation-workflow",
        "Project escalation workflow",
        "Read the supporting guide for PMO and assurance teams.",
      ),
    ],
  }),
  createPage({
    key: "guideEvaluateBusinessIdeas",
    path: "/learn/how-to-evaluate-business-ideas",
    type: "guide",
    title: "How to Evaluate Business Ideas More Rigorously",
    seoTitle: "How to Evaluate Business Ideas | Compelion",
    seoDescription:
      "Learn how to evaluate business ideas using clearer criteria, stronger questions, and a more consistent decision framework before sponsors commit time or budget.",
    intro:
      "Most business ideas are rejected or approved for the wrong reasons. Strong evaluation is less about intuition and more about asking the right questions in the right sequence.",
    bullets: [
      "Define the problem before judging the idea",
      "Test value, feasibility, evidence, and strategic fit",
      "Use a repeatable process instead of personality-driven review",
    ],
    stats: [
      { value: "4 lenses", label: "problem, value, feasibility, and fit" },
      { value: "Repeatable", label: "for sponsor and team review" },
      { value: "Commercially linked", label: "to idea evaluation software" },
    ],
    primaryImage: heroIllustrations.engagingCulture,
    secondaryImage: heroScreenshots.ideas,
    capabilities: [
      {
        title: "Clarify the problem",
        text: "A weakly defined problem makes even a good idea hard to evaluate properly.",
      },
      {
        title: "Test value honestly",
        text: "Ask what will improve, for whom, and how that change will be measured.",
      },
      {
        title: "Assess feasibility",
        text: "Review capability, dependencies, timing, and execution risk before getting carried away with upside.",
      },
      {
        title: "Check strategic fit",
        text: "Compare the idea against current priorities so resources do not drift into low-fit work.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Frame the problem",
        text: "Define the situation the idea is trying to improve.",
      },
      {
        step: "02",
        title: "Assess value",
        text: "Check expected benefits and who experiences them.",
      },
      {
        step: "03",
        title: "Test feasibility",
        text: "Review constraints, dependencies, and delivery realism.",
      },
      {
        step: "04",
        title: "Decide the next step",
        text: "Refine, reject, or prepare the idea for deeper sponsorship review.",
      },
    ],
    fit: [
      "Innovation leads",
      "Transformation teams",
      "Managers reviewing new initiative proposals",
    ],
    proofPoints: [
      "Good evaluation frameworks reduce both false positives and missed opportunities.",
      "This guide should support the commercial page by solving the informational version of the same intent.",
      "The strongest CTA from this guide is into the idea evaluation template or software page.",
    ],
    faqs: [
      {
        question: "What is the biggest mistake in idea evaluation?",
        answer:
          "Jumping to the solution before the problem, value, and feasibility are clearly understood.",
      },
      {
        question: "Can a simple scoring model be enough?",
        answer:
          "Sometimes, but only if the criteria reflect the real decision factors and are used consistently.",
      },
      {
        question: "How does this guide connect to Compelion?",
        answer:
          "Compelion operationalises this same evaluation discipline inside a structured software workflow.",
      },
    ],
    relatedLinks: [
      related(
        "/idea-evaluation-software",
        "Idea evaluation software",
        "Move from the guide to the product page.",
      ),
      related(
        "/templates/idea-evaluation-template",
        "Idea evaluation template",
        "Use the practical resource version of this guide.",
      ),
      related(
        "/learn/innovation-prioritisation-framework",
        "Innovation prioritisation framework",
        "Continue to the next guide in the same cluster.",
      ),
    ],
  }),
  createPage({
    key: "guideInnovationPrioritisationFramework",
    path: "/learn/innovation-prioritisation-framework",
    type: "guide",
    title: "Innovation Prioritisation Framework for Better Portfolio Decisions",
    seoTitle: "Innovation Prioritisation Framework | Compelion",
    seoDescription:
      "Use this innovation prioritisation framework to compare initiatives with clearer criteria, better sequencing logic, and stronger governance around portfolio decisions.",
    intro:
      "Prioritisation frameworks are useful only when they make trade-offs visible. This guide explains how to compare innovation initiatives without pretending every decision can be reduced to a spreadsheet score.",
    bullets: [
      "Prioritise against value, fit, urgency, and feasibility",
      "Use the framework to support portfolio sequencing, not replace judgment",
      "Avoid false precision and criteria overload",
    ],
    stats: [
      {
        value: "Portfolio focused",
        label: "for comparing multiple initiatives",
      },
      { value: "Decision oriented", label: "for sequencing and sponsorship" },
      { value: "Software linked", label: "to portfolio management workflows" },
    ],
    primaryImage: heroIllustrations.performanceMetrics,
    secondaryImage: heroScreenshots.orgLeaderboard,
    capabilities: [
      {
        title: "Choose the right criteria",
        text: "Focus on the decision drivers that actually matter rather than bloating the framework with everything imaginable.",
      },
      {
        title: "Compare across the portfolio",
        text: "Look at the relative value and timing of initiatives, not just their individual attractiveness.",
      },
      {
        title: "Discuss trade-offs explicitly",
        text: "Use the framework to surface conflicts around capacity, urgency, and strategic fit.",
      },
      {
        title: "Keep judgment in the loop",
        text: "A framework improves the decision conversation; it does not eliminate leadership responsibility.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Define decision criteria",
        text: "Agree what matters most for the portfolio.",
      },
      {
        step: "02",
        title: "Score consistently",
        text: "Assess each initiative using a shared structure.",
      },
      {
        step: "03",
        title: "Review trade-offs",
        text: "Discuss what the scores mean in context rather than blindly ranking.",
      },
      {
        step: "04",
        title: "Sequence the portfolio",
        text: "Decide what moves now, later, or not at all.",
      },
    ],
    fit: [
      "Innovation portfolio leaders",
      "Transformation offices",
      "PMOs managing strategic initiative sequencing",
    ],
    proofPoints: [
      "This guide supports buyers who know they need a better prioritisation model but are not yet shopping for software by name.",
      "The educational layer makes the commercial portfolio page easier to rank and easier to understand.",
      "The strongest conversion paths from here are into the prioritisation matrix and portfolio software pages.",
    ],
    faqs: [
      {
        question:
          "What criteria should an innovation prioritisation framework include?",
        answer:
          "Usually some mix of value, strategic fit, urgency, feasibility, risk, and capacity impact.",
      },
      {
        question: "Should every initiative be forced into the same model?",
        answer:
          "A common framework helps comparison, but leadership still needs to interpret differences in context.",
      },
      {
        question: "How does this link to Compelion?",
        answer:
          "Compelion helps teams operationalise prioritisation logic inside a live portfolio management and governance workflow.",
      },
    ],
    relatedLinks: [
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Move from the guide to the product page.",
      ),
      related(
        "/templates/project-prioritisation-matrix",
        "Project prioritisation matrix",
        "Use the practical tool version of this framework.",
      ),
      related(
        "/solutions/transformation-offices",
        "Transformation offices",
        "See the use-case page that aligns with this guide.",
      ),
    ],
  }),
  createPage({
    key: "guideProjectEscalationWorkflow",
    path: "/learn/project-escalation-workflow",
    type: "guide",
    title: "Project Escalation Workflow for Earlier, Better Intervention",
    seoTitle: "Project Escalation Workflow | Compelion",
    seoDescription:
      "Learn how to design a project escalation workflow that surfaces risk earlier, clarifies ownership, and helps leadership intervene with better delivery context.",
    intro:
      "Escalation should not mean panic. A strong escalation workflow helps teams surface the right issues early enough that action is still useful.",
    bullets: [
      "Define what must be escalated and by whom",
      "Make urgency and ownership explicit",
      "Link escalation decisions to real delivery context",
    ],
    stats: [
      { value: "Clearer ownership", label: "for high-priority issues" },
      { value: "Earlier action", label: "through better visibility" },
      {
        value: "Commercially linked",
        label: "to escalation software and PMO use cases",
      },
    ],
    primaryImage: heroIllustrations.unifiedCommunication,
    secondaryImage: heroScreenshots.notifications,
    capabilities: [
      {
        title: "Define escalation triggers",
        text: "Clarify which signals require action rather than leaving escalation to personality and confidence levels.",
      },
      {
        title: "Route to the right level",
        text: "Match the issue to the governance forum or leader who can actually resolve it.",
      },
      {
        title: "Keep the context attached",
        text: "Escalations should travel with the project, timing, and impact context behind them.",
      },
      {
        title: "Track outcomes",
        text: "A good escalation workflow records action, not just the fact that a concern was raised.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Set thresholds",
        text: "Define what counts as a true escalation event.",
      },
      {
        step: "02",
        title: "Assign ownership",
        text: "Clarify who raises, receives, and resolves escalation items.",
      },
      {
        step: "03",
        title: "Route with context",
        text: "Attach delivery, timing, and impact information to the escalation.",
      },
      {
        step: "04",
        title: "Follow through",
        text: "Track decisions and actions to closure.",
      },
    ],
    fit: [
      "PMOs and assurance teams",
      "Programme leaders",
      "Transformation offices with complex initiative portfolios",
    ],
    proofPoints: [
      "Escalation guides rank because teams often have weak processes even when they already use project tools.",
      "The strongest conversion path is into the project risk and escalation management page.",
      "This guide should stay practical and operational rather than theoretical.",
    ],
    faqs: [
      {
        question: "What is the most common failure in escalation workflows?",
        answer:
          "Ambiguity about what should be escalated, who owns it, and how quickly action is expected.",
      },
      {
        question: "Should every problem be escalated?",
        answer:
          "No. Good workflows set clear thresholds so teams escalate the right issues rather than everything.",
      },
      {
        question: "How does this connect to Compelion?",
        answer:
          "Compelion provides a live operational surface for escalation, assurance, and decision workflows rather than leaving them in static documents alone.",
      },
    ],
    relatedLinks: [
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "Move from the guide to the product page.",
      ),
      related(
        "/solutions/pmo",
        "PMO solution page",
        "See the use-case page most closely aligned with escalation needs.",
      ),
      related(
        "/compare/asana-alternative",
        "Asana alternative",
        "Use the comparison page if the governance-vs-task-tool question is still open.",
      ),
    ],
  }),
  createPage({
    key: "guideWriteBusinessCase",
    path: "/learn/how-to-write-a-business-case",
    type: "guide",
    title: "How to Write a Business Case That Holds Up Under Review",
    seoTitle: "How to Write a Business Case | Compelion",
    seoDescription:
      "Learn how to write a business case that explains the problem, expected value, delivery implications, and decision rationale clearly enough for sponsor review.",
    intro:
      "A strong business case makes the logic of change clear. This guide explains how to write one that is persuasive because it is rigorous, not because it is padded with jargon.",
    bullets: [
      "Explain the problem before selling the solution",
      "Make value, risk, and delivery implications visible",
      "Write for sponsor review, not just internal approval theatre",
    ],
    stats: [
      { value: "Decision ready", label: "for sponsor and governance review" },
      { value: "Practical", label: "for real initiative proposals" },
      {
        value: "Commercially linked",
        label: "to templates and AI-assisted case generation",
      },
    ],
    secondaryImage: heroScreenshots.executiveSummary,
    capabilities: [
      {
        title: "State the problem clearly",
        text: "If the current-state issue is vague, the case for change will stay weak.",
      },
      {
        title: "Describe the proposed intervention",
        text: "Make the option concrete enough that reviewers understand what is actually being asked for.",
      },
      {
        title: "Explain value and implications",
        text: "Cover benefits, risks, cost, delivery dependencies, and likely impact honestly.",
      },
      {
        title: "Make the decision easy to understand",
        text: "A good business case helps sponsors see what they are approving, challenging, or rejecting.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Frame the problem",
        text: "Define the issue, opportunity, or strategic pressure.",
      },
      {
        step: "02",
        title: "Present the case",
        text: "Explain the proposal and expected outcomes.",
      },
      {
        step: "03",
        title: "Assess realism",
        text: "Cover cost, risk, implementation needs, and constraints.",
      },
      {
        step: "04",
        title: "Support the decision",
        text: "Prepare the case so reviewers can evaluate it efficiently.",
      },
    ],
    fit: [
      "Innovation sponsors",
      "Transformation teams",
      "Project leads preparing initiative proposals",
    ],
    proofPoints: [
      "The guide earns traffic for informational intent while feeding a clear commercial path into business-case software and templates.",
      "It is important not to overcomplicate the business case; buyers respond better to rigor than jargon.",
      "This page should internally link both to the template and the AI business case generator page.",
    ],
    faqs: [
      {
        question: "What makes a business case persuasive?",
        answer:
          "Clarity, evidence, realism, and a direct connection between the problem, the proposal, and the value expected.",
      },
      {
        question: "Should a business case include risks and delivery issues?",
        answer:
          "Yes. Hiding constraints weakens the case because sponsors need to understand implementation implications before committing.",
      },
      {
        question: "How does this relate to Compelion?",
        answer:
          "Compelion helps teams structure and accelerate business-case development inside a workflow linked to evaluation and delivery governance.",
      },
    ],
    relatedLinks: [
      related(
        "/ai-business-case-generator",
        "AI business case generator",
        "Move from the guide to the software page.",
      ),
      related(
        "/templates/business-case-template",
        "Business case template",
        "Use the resource page that supports this same intent.",
      ),
      related(
        "/learn/how-to-evaluate-business-ideas",
        "How to evaluate business ideas",
        "See the earlier-stage guide that feeds into business-case writing.",
      ),
    ],
  }),
  createPage({
    key: "guideHealthcareInnovationGovernance",
    path: "/learn/healthcare-innovation-governance",
    type: "guide",
    title: "Healthcare Innovation Governance That Improves Decision Quality",
    seoTitle: "Healthcare Innovation Governance | Compelion",
    seoDescription:
      "Learn how healthcare innovation governance can improve proposal quality, prioritisation, review discipline, and follow-through across innovation and improvement portfolios.",
    intro:
      "Healthcare innovation governance has to balance safety, evidence, operational practicality, and strategic value. This guide explains how to create a governance model that improves decision quality rather than slowing everything down.",
    bullets: [
      "Create clearer governance criteria for innovation and improvement ideas",
      "Improve the quality of proposals reaching review forums",
      "Connect approval decisions to visible delivery follow-through",
    ],
    stats: [
      {
        value: "Governance focused",
        label: "for healthcare innovation and improvement work",
      },
      { value: "Operational", label: "rather than purely theoretical" },
      {
        value: "Commercially linked",
        label: "to the healthcare solution and software pages",
      },
    ],
    primaryImage: heroIllustrations.sentimentAnalysis,
    secondaryImage: heroScreenshots.divisionLeaderboard,
    capabilities: [
      {
        title: "Define review criteria",
        text: "Clarify what evidence, value, feasibility, and risk information must be present before ideas move forward.",
      },
      {
        title: "Improve sponsor review quality",
        text: "Help innovation and improvement boards spend their time on better-prepared proposals.",
      },
      {
        title: "Create governance continuity",
        text: "Keep the reasoning behind approvals visible through delivery and review cycles.",
      },
      {
        title: "Avoid process theatre",
        text: "Governance should make decisions stronger, not simply add forms and delays.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Set governance rules",
        text: "Define what information and evidence a proposal must include.",
      },
      {
        step: "02",
        title: "Improve intake quality",
        text: "Raise the standard before ideas arrive at the board or review group.",
      },
      {
        step: "03",
        title: "Review consistently",
        text: "Assess innovation and improvement ideas using a shared logic.",
      },
      {
        step: "04",
        title: "Track follow-through",
        text: "Keep approved initiatives visible after governance decisions are made.",
      },
    ],
    fit: [
      "Healthcare innovation leads",
      "Quality improvement governance groups",
      "Operational and transformation leaders in healthcare settings",
    ],
    proofPoints: [
      "Healthcare buyers are usually sensitive to governance weakness, which makes this guide commercially relevant as well as educational.",
      "The best internal links from here are to the healthcare commercial page and idea evaluation workflows.",
      "The guide should frame governance as a decision-quality lever, not a compliance burden alone.",
    ],
    faqs: [
      {
        question: "What is the purpose of healthcare innovation governance?",
        answer:
          "To improve the quality, safety, value, and prioritisation of innovation and improvement decisions while maintaining practical delivery follow-through.",
      },
      {
        question: "How do you stop governance from becoming bureaucracy?",
        answer:
          "Use only the criteria and process steps that improve decision quality. Remove steps that do not change the outcome.",
      },
      {
        question: "How does this connect to Compelion?",
        answer:
          "Compelion helps teams operationalise stronger healthcare innovation governance through structured evaluation, business-case support, portfolio visibility, and delivery tracking.",
      },
    ],
    relatedLinks: [
      related(
        "/healthcare-innovation-management-software",
        "Healthcare innovation management software",
        "Move from the guide to the healthcare commercial page.",
      ),
      related(
        "/solutions/healthcare",
        "Healthcare solution page",
        "See the broader healthcare solution framing.",
      ),
      related(
        "/templates/idea-evaluation-template",
        "Idea evaluation template",
        "Use the practical resource that supports healthcare review workflows.",
      ),
    ],
  }),
  createPage({
    key: "projectPrioritisationSoftware",
    path: "/project-prioritisation-software",
    type: "commercial",
    title:
      "Project Prioritisation Software for Better Investment and Sequencing Decisions",
    seoTitle: "Project Prioritisation Software | Compelion",
    seoDescription:
      "Compelion helps teams prioritise projects with clearer criteria, stronger business-case quality, and portfolio-level visibility across competing initiatives.",
    intro:
      "Project prioritisation fails when every proposal arrives half-formed and every sponsor treats their request as urgent. Compelion helps teams compare initiatives with more discipline and context.",
    bullets: [
      "Improve the quality of projects entering prioritisation",
      "Use a shared decision framework across competing work",
      "Link prioritisation decisions to delivery and escalation visibility",
    ],
    stats: [
      { value: "Shared criteria", label: "for more consistent prioritisation" },
      {
        value: "Portfolio context",
        label: "for sequencing and sponsorship decisions",
      },
      {
        value: "Governance linked",
        label: "to delivery visibility and escalation",
      },
    ],
    primaryImage: heroIllustrations.goalTracking,
    secondaryImage: heroScreenshots.teamLeaderboard,
    capabilities: [
      {
        title: "Higher-quality inputs",
        text: "Improve the quality of proposals before they reach prioritisation discussions.",
      },
      {
        title: "Comparability",
        text: "Use a common framework across projects so prioritisation meetings become more rigorous.",
      },
      {
        title: "Sequencing support",
        text: "Balance urgency, value, feasibility, and capacity rather than ranking on politics alone.",
      },
      {
        title: "Follow-through visibility",
        text: "Keep the decision logic connected to the work as it progresses into delivery.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Prepare",
        text: "Improve project proposal quality and consistency.",
      },
      {
        step: "02",
        title: "Compare",
        text: "Review projects against common decision criteria.",
      },
      {
        step: "03",
        title: "Sequence",
        text: "Decide what should happen now, later, or not at all.",
      },
      {
        step: "04",
        title: "Track",
        text: "Monitor prioritised work through delivery and governance.",
      },
    ],
    fit: [
      "PMOs and portfolio teams",
      "Transformation offices dealing with heavy project demand",
      "Leaders choosing between competing initiatives",
    ],
    proofPoints: [
      "Prioritisation is strongest when it starts with better proposal quality, not only better scoring meetings.",
      "This page sits naturally between the portfolio and template clusters.",
      "The best supporting assets here are the prioritisation matrix and framework guide.",
    ],
    faqs: [
      {
        question: "What is project prioritisation software for?",
        answer:
          "It helps teams compare competing projects, improve decision consistency, and make sequencing choices with better evidence and context.",
      },
      {
        question: "Why not use a spreadsheet alone?",
        answer:
          "Spreadsheets can help, but they usually struggle when proposal quality, governance continuity, and delivery visibility need to stay connected.",
      },
      {
        question: "How does this relate to Compelion's portfolio view?",
        answer:
          "The prioritisation workflow feeds directly into the broader portfolio management and governance model inside Compelion.",
      },
    ],
    relatedLinks: [
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Return to the broader portfolio page.",
      ),
      related(
        "/templates/project-prioritisation-matrix",
        "Project prioritisation matrix",
        "Use the practical template that supports this product page.",
      ),
      related(
        "/learn/innovation-prioritisation-framework",
        "Innovation prioritisation framework",
        "Read the guide that supports this same intent.",
      ),
    ],
  }),
  createPage({
    key: "deliveryAssuranceAnalytics",
    path: "/delivery-assurance-analytics",
    type: "commercial",
    title: "Delivery Assurance Analytics for Portfolio and Programme Oversight",
    seoTitle: "Delivery Assurance Analytics | Compelion",
    seoDescription:
      "Compelion helps teams track delivery assurance signals, escalations, and project health with clearer operational visibility across active initiative portfolios.",
    intro:
      "Delivery assurance works best when leaders can see more than summary status. Compelion helps make assurance, escalation, and portfolio signals visible enough to act on.",
    bullets: [
      "Bring assurance and escalation signals into one operating picture",
      "See portfolio health with more context than a static status report",
      "Support better intervention and steering decisions",
    ],
    stats: [
      {
        value: "Signal driven",
        label: "for assurance and escalation workflows",
      },
      { value: "Portfolio aware", label: "across active project stages" },
      {
        value: "Intervention ready",
        label: "for governance and leadership action",
      },
    ],
    primaryImage: heroIllustrations.advancedForecasting,
    secondaryImage: heroScreenshots.orgLeaderboard,
    capabilities: [
      {
        title: "Assurance visibility",
        text: "See where delivery confidence is weakening before issues become executive surprises.",
      },
      {
        title: "Escalation awareness",
        text: "Track high-priority items, decisions, and overdue actions in one operating surface.",
      },
      {
        title: "Project health context",
        text: "Understand assurance signals in relation to the projects and stages they affect.",
      },
      {
        title: "Governance support",
        text: "Help sponsors and PMOs steer active portfolios with better evidence.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Observe",
        text: "Collect assurance, escalation, and project-health signals.",
      },
      {
        step: "02",
        title: "Interpret",
        text: "Assess which signals matter most and why.",
      },
      {
        step: "03",
        title: "Intervene",
        text: "Route issues to the right sponsor, PMO, or governance forum.",
      },
      {
        step: "04",
        title: "Learn",
        text: "Use the analytics picture to improve portfolio control over time.",
      },
    ],
    fit: [
      "Delivery assurance teams",
      "PMOs with high-risk initiative portfolios",
      "Transformation leaders needing earlier visibility into trouble signals",
    ],
    proofPoints: [
      "Assurance analytics are valuable when leadership needs more than static RAG reporting.",
      "This page complements the escalation workflow and PMO solution pages directly.",
      "The strongest buyer pain here is late intervention caused by weak visibility.",
    ],
    faqs: [
      {
        question: "What are delivery assurance analytics?",
        answer:
          "They are the signals, trends, and operational indicators leaders use to understand whether a project or portfolio is genuinely healthy, deteriorating, or at risk of escalation.",
      },
      {
        question: "How is this different from project reporting?",
        answer:
          "Delivery assurance analytics focus on intervention-ready signals and governance context rather than just summarising progress updates.",
      },
      {
        question: "Who needs this most?",
        answer:
          "PMOs, delivery assurance leads, and transformation offices managing multiple active initiatives with uneven risk profiles.",
      },
    ],
    relatedLinks: [
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "See the adjacent workflow page for delivery governance.",
      ),
      related(
        "/solutions/pmo",
        "PMO solution page",
        "See the use-case page most aligned with delivery assurance buyers.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Return to the portfolio page that surrounds these analytics.",
      ),
    ],
  }),
  createPage({
    key: "solutionStrategyTeams",
    path: "/solutions/strategy-teams",
    type: "solution",
    title: "Compelion for Strategy Teams",
    seoTitle: "Strategy Team Software | Compelion",
    seoDescription:
      "Compelion helps strategy teams evaluate opportunities, prioritise strategic initiatives, build stronger cases for change, and keep sponsored work visible through delivery.",
    intro:
      "Strategy teams often know which ambitions matter but struggle to create a disciplined operating model around initiative quality, prioritisation, and follow-through. Compelion helps close that gap.",
    bullets: [
      "Improve strategic initiative intake quality",
      "Prioritise with more discipline and comparability",
      "Keep approved strategic work visible after sponsorship",
    ],
    stats: [
      {
        value: "Strategy aligned",
        label: "for initiative selection and sequencing",
      },
      { value: "Case oriented", label: "for stronger sponsor discussions" },
      {
        value: "Execution linked",
        label: "to portfolio and assurance visibility",
      },
    ],
    primaryImage: heroIllustrations.dynamicStrategy,
    secondaryImage: heroScreenshots.teamLeaderboard,
    capabilities: [
      {
        title: "Opportunity evaluation",
        text: "Assess initiatives against strategic fit, value, and delivery realism.",
      },
      {
        title: "Sponsorship discipline",
        text: "Improve the discipline of what gets sponsored and what does not.",
      },
      {
        title: "Executive-ready cases",
        text: "Give strategy teams stronger material for executive review.",
      },
      {
        title: "Delivery continuity",
        text: "Keep strategic decisions connected to delivery outcomes.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Shape demand",
        text: "Improve the quality of strategic initiative proposals.",
      },
      {
        step: "02",
        title: "Prioritise",
        text: "Compare strategic options against shared criteria.",
      },
      {
        step: "03",
        title: "Sponsor",
        text: "Support better executive decisions with stronger cases.",
      },
      {
        step: "04",
        title: "Track",
        text: "Maintain visibility after sponsorship and launch.",
      },
    ],
    fit: [
      "Corporate strategy teams",
      "Enterprise planning functions",
      "Leaders coordinating strategic initiative portfolios",
    ],
    proofPoints: [
      "Strategy teams often need a better operating layer between ambition and delivery execution.",
      "This use-case page is naturally linked to prioritisation and business-case content.",
      "It works best when framed around decision quality rather than generic project control.",
    ],
    faqs: [
      {
        question: "How does Compelion help strategy teams?",
        answer:
          "It helps them evaluate, prioritise, justify, and track strategic initiatives with more governance discipline and better delivery continuity.",
      },
      {
        question: "Is this just another project tracking tool?",
        answer:
          "No. The emphasis here is on better strategy-to-execution operating logic rather than only task coordination.",
      },
      {
        question: "Which other pages should strategy teams explore?",
        answer:
          "The project prioritisation, business-case, and portfolio management pages are the strongest next steps.",
      },
    ],
    relatedLinks: [
      related(
        "/project-prioritisation-software",
        "Project prioritisation software",
        "See the product page most aligned with strategic sequencing decisions.",
      ),
      related(
        "/ai-business-case-generator",
        "AI business case generator",
        "See how strategy teams can accelerate stronger business cases.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "Return to the broader portfolio view.",
      ),
    ],
  }),
  createPage({
    key: "solutionHealthcare",
    path: "/solutions/healthcare",
    type: "solution",
    title: "Compelion for Healthcare Innovation and Improvement Teams",
    seoTitle: "Healthcare Innovation and Improvement Software | Compelion",
    seoDescription:
      "Compelion helps healthcare teams evaluate improvement ideas, strengthen governance, prioritise initiatives, and monitor delivery across innovation and quality improvement portfolios.",
    intro:
      "Healthcare teams need governance rigor without choking innovation. Compelion helps create a more practical operating model around innovation, improvement, and delivery oversight.",
    bullets: [
      "Improve healthcare proposal quality before governance review",
      "Prioritise innovation and improvement work more clearly",
      "Track approved initiatives with better delivery and escalation visibility",
    ],
    stats: [
      {
        value: "Healthcare aware",
        label: "for evidence-heavy change environments",
      },
      {
        value: "Governance focused",
        label: "for innovation and improvement review",
      },
      { value: "Operationally linked", label: "to delivery follow-through" },
    ],
    primaryImage: heroIllustrations.customerVoices,
    secondaryImage: heroScreenshots.teamView,
    capabilities: [
      {
        title: "Proposal quality",
        text: "Strengthen innovation and improvement submissions before they reach governance forums.",
      },
      {
        title: "Evaluation discipline",
        text: "Use clearer criteria for evaluating patient, operational, and delivery value.",
      },
      {
        title: "Portfolio visibility",
        text: "See which initiatives are progressing, delayed, parked, or in need of intervention.",
      },
      {
        title: "Governance continuity",
        text: "Keep the rationale behind approvals visible after review decisions are made.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Capture opportunities",
        text: "Collect improvement and innovation ideas from healthcare teams.",
      },
      {
        step: "02",
        title: "Evaluate and govern",
        text: "Review ideas with stronger evidence and prioritisation logic.",
      },
      {
        step: "03",
        title: "Approve and sponsor",
        text: "Support better governance and sponsor decisions.",
      },
      {
        step: "04",
        title: "Track delivery",
        text: "Monitor implementation and escalation after approval.",
      },
    ],
    fit: [
      "Healthcare innovation teams",
      "Quality improvement teams",
      "Healthcare transformation offices",
    ],
    proofPoints: [
      "Healthcare buyers care about governance quality and operational practicality at the same time.",
      "This solution page supports both the healthcare commercial page and the governance guide.",
      "The strongest linked assets here are evaluation templates and healthcare governance content.",
    ],
    faqs: [
      {
        question: "Who is this healthcare solution page for?",
        answer:
          "It is for healthcare innovation, improvement, and transformation teams that need a stronger operating model around idea quality, governance, and delivery follow-through.",
      },
      {
        question: "Is this different from the healthcare commercial page?",
        answer:
          "Yes. The commercial page targets category intent, while this page speaks directly to the healthcare team context and operating need.",
      },
      {
        question: "What should a healthcare buyer read next?",
        answer:
          "The healthcare commercial page and governance guide are the best next steps.",
      },
    ],
    relatedLinks: [
      related(
        "/healthcare-innovation-management-software",
        "Healthcare innovation management software",
        "Return to the healthcare commercial page.",
      ),
      related(
        "/learn/healthcare-innovation-governance",
        "Healthcare innovation governance",
        "Read the guide that supports this solution page.",
      ),
      related(
        "/templates/idea-evaluation-template",
        "Idea evaluation template",
        "Use the practical resource that supports healthcare review workflows.",
      ),
    ],
  }),
  createPage({
    key: "solutionQualityImprovement",
    path: "/solutions/quality-improvement",
    type: "solution",
    title: "Compelion for Quality Improvement Teams",
    seoTitle: "Quality Improvement Project Software | Compelion",
    seoDescription:
      "Compelion helps quality improvement teams evaluate proposals, prioritise change work, support governance, and maintain visibility across active improvement projects.",
    intro:
      "Quality improvement teams need a better bridge between frontline ideas, governance review, and delivery follow-through. Compelion helps make the QI pipeline more disciplined and visible.",
    bullets: [
      "Improve the quality of QI proposals",
      "Prioritise improvement work with stronger comparability",
      "Track approved changes through delivery and escalation",
    ],
    stats: [
      { value: "QI focused", label: "for structured improvement work" },
      { value: "Governance aware", label: "for review and approval forums" },
      { value: "Delivery linked", label: "to stage and escalation visibility" },
    ],
    primaryImage: heroIllustrations.sentimentAnalysis,
    secondaryImage: heroScreenshots.teamLeaderboard,
    capabilities: [
      {
        title: "Proposal discipline",
        text: "Give quality improvement teams a stronger framework for assessing change ideas before approval.",
      },
      {
        title: "Prioritisation support",
        text: "Compare improvement initiatives with more structure and less noise.",
      },
      {
        title: "Governance continuity",
        text: "Keep approval logic and decision context visible through delivery.",
      },
      {
        title: "Risk visibility",
        text: "Track issues and escalation items once work is active.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Capture ideas",
        text: "Collect improvement opportunities from frontline teams.",
      },
      {
        step: "02",
        title: "Review and prioritise",
        text: "Assess expected benefit, feasibility, and delivery implications.",
      },
      {
        step: "03",
        title: "Approve",
        text: "Support clearer sponsor and governance decisions.",
      },
      {
        step: "04",
        title: "Monitor",
        text: "Track improvement projects through delivery and escalation.",
      },
    ],
    fit: [
      "Quality improvement programmes",
      "Operational improvement teams",
      "Healthcare and service-delivery improvement leaders",
    ],
    proofPoints: [
      "QI teams often need better structure before and after approval, not only project tasking.",
      "This use-case page pairs naturally with the healthcare and evaluation clusters.",
      "The strongest supporting assets are the evaluation template and healthcare governance guide.",
    ],
    faqs: [
      {
        question:
          "What problem does Compelion solve for quality improvement teams?",
        answer:
          "It helps them evaluate, prioritise, govern, and track improvement work with better continuity across the full operating workflow.",
      },
      {
        question: "Is this only for healthcare quality improvement?",
        answer:
          "No. The page is relevant to broader operational improvement contexts too, although healthcare is a strong use case.",
      },
      {
        question: "What should QI teams explore next?",
        answer:
          "The healthcare solution and evaluation template pages are strong next steps.",
      },
    ],
    relatedLinks: [
      related(
        "/solutions/healthcare",
        "Healthcare solution page",
        "See the broader healthcare framing connected to quality improvement.",
      ),
      related(
        "/templates/idea-evaluation-template",
        "Idea evaluation template",
        "Use the practical resource that helps teams structure review.",
      ),
      related(
        "/healthcare-innovation-management-software",
        "Healthcare innovation management software",
        "Return to the healthcare category page.",
      ),
    ],
  }),
  createPage({
    key: "aworkAlternative",
    path: "/compare/awork-alternative",
    type: "comparison",
    title:
      "Compelion vs Awork Alternative for Governance-Heavy Initiative Portfolios",
    seoTitle: "Awork Alternative for Strategic Initiative Teams | Compelion",
    seoDescription:
      "Compare Compelion with Awork when your team needs idea evaluation, prioritisation, governance, and delivery assurance rather than broad work management alone.",
    intro:
      "Awork is a work-management option. Compelion is the better fit when the buyer needs a stronger operating model around proposal quality, prioritisation, governance, and assurance.",
    bullets: [
      "Compare category fit before feature checklists",
      "Use Compelion when governance and prioritisation matter more",
      "Use Awork when generic work coordination is the main requirement",
    ],
    stats: [
      { value: "Governance stronger", label: "for Compelion's category fit" },
      {
        value: "Broader work management",
        label: "for Awork's main proposition",
      },
      { value: "Higher intent", label: "for alternative-search buyers" },
    ],
    primaryImage: heroIllustrations.unifiedCommunication,
    secondaryImage: heroScreenshots.teamView,
    capabilities: [
      {
        title: "Upstream evaluation",
        text: "Compelion supports better initiative quality before the work is merely tracked.",
      },
      {
        title: "Prioritisation and governance",
        text: "The platform is stronger where decisions, approvals, and sequencing matter.",
      },
      {
        title: "Assurance visibility",
        text: "Compelion supports escalation and assurance workflows alongside project context.",
      },
      {
        title: "Category clarity",
        text: "This page helps buyers avoid choosing a general work tool for a governance-heavy problem.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Clarify the need",
        text: "Check whether the problem is work coordination or initiative governance.",
      },
      {
        step: "02",
        title: "Compare workflows",
        text: "Assess intake, prioritisation, business-case, and escalation depth.",
      },
      {
        step: "03",
        title: "Review fit",
        text: "Use the product views to test against your operating model.",
      },
      {
        step: "04",
        title: "Choose on category",
        text: "Select the product that matches the real job to be done.",
      },
    ],
    fit: [
      "Buyers comparing Compelion with European work-management alternatives",
      "PMOs and transformation teams needing more governance depth",
      "Innovation teams that have outgrown generic work tools",
    ],
    proofPoints: [
      "Alternative pages work best when they clarify fit honestly rather than claiming every feature battle.",
      "Compelion's edge is in evaluation, governance, and assurance depth rather than generic project breadth.",
      "This page should internally link back to the main innovation and portfolio categories.",
    ],
    comparison: {
      headers: ["Evaluation area", "Compelion", "Awork"],
      rows: comparisonRows([
        {
          topic: "Idea evaluation",
          left: "Purpose-built and structured",
          right: "Not the core product story",
        },
        {
          topic: "Portfolio governance",
          left: "Yes",
          right: "General work-management orientation",
        },
        {
          topic: "Escalation and assurance",
          left: "Yes",
          right: "Less central to category fit",
        },
        {
          topic: "Best fit",
          left: "Strategic initiative and governance-heavy teams",
          right: "Broad work and project coordination",
        },
      ]),
    },
    faqs: [
      {
        question: "Is Compelion directly comparable to Awork?",
        answer:
          "Only partially. This page is most useful when buyers are still clarifying the right category for their operating problem.",
      },
      {
        question: "Who should pick Compelion here?",
        answer:
          "Teams that need initiative evaluation, prioritisation, business-case support, and governance visibility rather than only work coordination.",
      },
      {
        question: "What next page should buyers visit?",
        answer:
          "The AI innovation management and portfolio pages are the strongest next steps.",
      },
    ],
    relatedLinks: [
      related(
        "/compare/monday-alternative",
        "Monday alternative",
        "Continue through the alternative page cluster.",
      ),
      related(
        "/ai-innovation-management-software",
        "AI innovation management software",
        "Return to the primary category page.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "See the portfolio page behind this comparison.",
      ),
    ],
  }),
  createPage({
    key: "mondayAlternative",
    path: "/compare/monday-alternative",
    type: "comparison",
    title:
      "Compelion vs Monday Alternative for Innovation, Governance, and Delivery Assurance",
    seoTitle:
      "Monday Alternative for Innovation and Governance Work | Compelion",
    seoDescription:
      "Compare Compelion with Monday when your team needs stronger initiative evaluation, prioritisation, governance, and assurance workflows instead of only broad project coordination.",
    intro:
      "Monday is broad and flexible for many team workflows. Compelion is a better fit when the work needs to be evaluated, prioritised, justified, and governed before execution tracking becomes the main concern.",
    bullets: [
      "Use Compelion when initiative quality and governance are the bottleneck",
      "Use Monday when broad configurable work management is the main need",
      "Choose based on operating model fit, not only interface preference",
    ],
    stats: [
      {
        value: "Governance oriented",
        label: "for Compelion's strongest use cases",
      },
      {
        value: "Configurable breadth",
        label: "for Monday's general work-management model",
      },
      {
        value: "Commercially high intent",
        label: "for alternative-search buyers",
      },
    ],
    primaryImage: heroIllustrations.automatedTaskManagement,
    secondaryImage: heroScreenshots.tools,
    capabilities: [
      {
        title: "Better upstream fit",
        text: "Compelion is built for stronger idea, business-case, and prioritisation workflows.",
      },
      {
        title: "Governance depth",
        text: "It is better suited to approvals, assurance, and escalation-heavy environments.",
      },
      {
        title: "Portfolio continuity",
        text: "Keep decision logic tied to delivery visibility rather than separating them into disconnected systems.",
      },
      {
        title: "Clearer category choice",
        text: "This page helps buyers who are comparing a specialist governance product with a broad work platform.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Define the operating gap",
        text: "Clarify whether your problem is coordination or initiative governance.",
      },
      {
        step: "02",
        title: "Compare workflows",
        text: "Review intake, case-building, prioritisation, and escalation support.",
      },
      {
        step: "03",
        title: "Assess portfolio needs",
        text: "Check whether leadership needs more decision and assurance context.",
      },
      {
        step: "04",
        title: "Choose on fit",
        text: "Select the platform that matches the real governance burden.",
      },
    ],
    fit: [
      "PMOs and transformation teams comparing broad alternatives",
      "Innovation leaders who need more than configurable boards",
      "Buyers with governance-heavy initiative portfolios",
    ],
    proofPoints: [
      "The stronger commercial story here is about category fit, not claiming to beat Monday at every generic work feature.",
      "Compelion wins where the buyer needs evaluation, business-case quality, prioritisation, assurance, and escalation in one operating model.",
      "This page should link into both portfolio and escalation pages to stay coherent.",
    ],
    comparison: {
      headers: ["Evaluation area", "Compelion", "Monday"],
      rows: comparisonRows([
        {
          topic: "Initiative evaluation",
          left: "Built for it",
          right: "Usually custom-built in boards and forms",
        },
        {
          topic: "Governance and approvals",
          left: "A core operating use case",
          right: "Possible, but not the main category story",
        },
        {
          topic: "Delivery assurance",
          left: "Connected to escalation and assurance workflows",
          right: "Broad work-management and automation flexibility",
        },
        {
          topic: "Best fit",
          left: "Strategic initiative governance",
          right: "General cross-functional project and work management",
        },
      ]),
    },
    faqs: [
      {
        question: "Should Monday users consider Compelion?",
        answer:
          "Yes, especially if they feel their biggest bottleneck is initiative quality, prioritisation, governance, or assurance rather than configurable project coordination.",
      },
      {
        question: "What makes Compelion the better fit here?",
        answer:
          "The stronger operating model around evaluation, justification, prioritisation, escalation, and portfolio continuity.",
      },
      {
        question: "What should buyers read next?",
        answer:
          "The AI innovation management, portfolio management, and escalation pages are the strongest next steps.",
      },
    ],
    relatedLinks: [
      related(
        "/compare/clickup-alternative",
        "ClickUp alternative",
        "Return to another broad-alternative comparison page.",
      ),
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "See the portfolio page behind this comparison.",
      ),
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "Explore the delivery-governance page next.",
      ),
    ],
  }),
  createPage({
    key: "riskEscalationTemplate",
    path: "/templates/risk-escalation-template",
    type: "resource",
    title: "Risk Escalation Template for Clearer Project Intervention",
    seoTitle: "Risk Escalation Template | Compelion",
    seoDescription:
      "Use this risk escalation template to define the issue, urgency, impact, ownership, and required decision before escalating to sponsors or governance forums.",
    intro:
      "A strong escalation template makes action easier. This page helps teams structure escalation items so leadership can understand and resolve them faster.",
    bullets: [
      "Clarify issue, impact, urgency, and owner",
      "Reduce vague escalation requests",
      "Support better PMO and sponsor intervention",
    ],
    stats: [
      { value: "Operational", label: "for real delivery and risk workflows" },
      {
        value: "Decision friendly",
        label: "for sponsors and governance forums",
      },
      {
        value: "Commercially linked",
        label: "to escalation and assurance software",
      },
    ],
    primaryImage: heroIllustrations.riskAssessment,
    secondaryImage: heroScreenshots.notifications,
    capabilities: [
      {
        title: "Better escalation quality",
        text: "Capture the essential information leaders need before they can intervene effectively.",
      },
      {
        title: "Faster understanding",
        text: "Reduce ambiguity around what has happened, why it matters, and what decision is needed.",
      },
      {
        title: "Stronger ownership",
        text: "Make the responsible owner and expected follow-up more explicit.",
      },
      {
        title: "Workflow bridge",
        text: "Move from a lightweight template into a live escalation and assurance workflow when needed.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Describe the issue",
        text: "Explain the problem or risk signal clearly.",
      },
      {
        step: "02",
        title: "Assess the impact",
        text: "Capture urgency, consequence, and timing.",
      },
      {
        step: "03",
        title: "Define the ask",
        text: "State what decision or intervention is needed.",
      },
      {
        step: "04",
        title: "Track follow-through",
        text: "Use the output to support action and closure.",
      },
    ],
    fit: [
      "PMOs and assurance teams",
      "Programme leaders",
      "Teams with immature escalation hygiene",
    ],
    proofPoints: [
      "Templates like this earn practical link value while supporting strong commercial alignment.",
      "This page is especially useful when teams know escalation is weak but are not yet ready for software rollout.",
      "The natural product destination from here is the project risk and escalation management page.",
    ],
    resourceItems: [
      "Issue description and current status",
      "Urgency, impact, and timeline",
      "Owner, affected initiative, and mitigation attempts",
      "Decision needed and follow-up action",
    ],
    faqs: [
      {
        question: "What should be in a risk escalation template?",
        answer:
          "The issue, impact, urgency, ownership, current mitigation, and the exact decision or support needed.",
      },
      {
        question: "Who should use this template?",
        answer:
          "PMOs, delivery assurance teams, programme leads, and anyone trying to improve escalation quality.",
      },
      {
        question: "How does this relate to Compelion?",
        answer:
          "Compelion helps teams operationalise the same escalation discipline inside a live workflow with project context and visibility.",
      },
    ],
    relatedLinks: [
      related(
        "/project-risk-and-escalation-management",
        "Project risk and escalation management",
        "Move from the template to the product page.",
      ),
      related(
        "/learn/project-escalation-workflow",
        "Project escalation workflow",
        "Read the supporting guide.",
      ),
      related(
        "/delivery-assurance-analytics",
        "Delivery assurance analytics",
        "See the analytics page that supports escalation visibility.",
      ),
    ],
  }),
  createPage({
    key: "benefitTrackingTemplate",
    path: "/templates/benefit-tracking-template",
    type: "resource",
    title: "Benefit Tracking Template for Initiative Value Realisation",
    seoTitle: "Benefit Tracking Template | Compelion",
    seoDescription:
      "Use this benefit tracking template to define expected outcomes, owners, measures, review cadence, and delivery dependencies for value realisation across projects and initiatives.",
    intro:
      "Benefits are often promised at approval and then forgotten during delivery. This template helps teams make value realisation more visible and accountable.",
    bullets: [
      "Track expected benefits beyond approval",
      "Assign ownership and review cadence",
      "Support stronger value-realisation discipline",
    ],
    stats: [
      { value: "Value focused", label: "for benefit and outcome tracking" },
      { value: "Practical", label: "for PMOs and transformation teams" },
      {
        value: "Commercially linked",
        label: "to portfolio and assurance workflows",
      },
    ],
    secondaryImage: heroScreenshots.orgLeaderboard,
    capabilities: [
      {
        title: "Expected-benefit clarity",
        text: "Capture what value is meant to change, for whom, and how success will be measured.",
      },
      {
        title: "Ownership discipline",
        text: "Assign who is responsible for tracking and reporting benefit progress.",
      },
      {
        title: "Review cadence",
        text: "Make benefit realisation a recurring governance topic instead of a one-time approval assumption.",
      },
      {
        title: "Delivery linkage",
        text: "Keep benefits connected to the projects and initiatives intended to deliver them.",
      },
    ],
    workflow: [
      {
        step: "01",
        title: "Define benefits",
        text: "Identify what change is expected and how it will be measured.",
      },
      {
        step: "02",
        title: "Assign ownership",
        text: "Clarify who is responsible for tracking value realisation.",
      },
      {
        step: "03",
        title: "Review regularly",
        text: "Check benefit progress on a defined cadence.",
      },
      {
        step: "04",
        title: "Act on variance",
        text: "Intervene when benefits are off-track or assumptions fail.",
      },
    ],
    fit: [
      "PMOs and value-realisation teams",
      "Transformation offices",
      "Leaders tracking outcomes across initiative portfolios",
    ],
    proofPoints: [
      "Benefit tracking is a natural extension of portfolio governance and delivery assurance.",
      "This template page should attract practical searches while feeding into the platform's broader operating model.",
      "The strongest linked pages here are the portfolio, business-case, and assurance pages.",
    ],
    resourceItems: [
      "Expected outcome and value statement",
      "Measure, baseline, and target",
      "Owner, cadence, and reporting route",
      "Dependencies, risks, and variance actions",
    ],
    faqs: [
      {
        question: "Why use a benefit tracking template?",
        answer:
          "Because benefits often disappear from view after approval unless ownership, measures, and review cadence are explicitly defined.",
      },
      {
        question: "Who should maintain it?",
        answer:
          "Usually the sponsor, PMO, value-realisation owner, or transformation lead responsible for ensuring the intended outcome is actually delivered.",
      },
      {
        question: "How does this connect to Compelion?",
        answer:
          "Compelion helps teams keep benefits visible alongside project progress, governance, and assurance workflows rather than in isolated spreadsheets.",
      },
    ],
    relatedLinks: [
      related(
        "/innovation-portfolio-management",
        "Innovation portfolio management",
        "See the broader portfolio page behind benefit tracking.",
      ),
      related(
        "/ai-business-case-generator",
        "AI business case generator",
        "Connect expected value back to stronger business-case development.",
      ),
      related(
        "/delivery-assurance-analytics",
        "Delivery assurance analytics",
        "See the analytics page that supports monitoring and intervention.",
      ),
    ],
  }),
];

export const seoPages = pages.map((page) => ({
  ...page,
  structuredData: buildStructuredData(page),
}));

export const seoPageMap = Object.fromEntries(
  seoPages.map((page) => [page.key, page]),
);

export const seoPagePathMap = Object.fromEntries(
  seoPages.map((page) => [page.path, page]),
);

export const seoPageRoutes = seoPages.map((page) => ({
  key: page.key,
  path: page.path,
}));

/* Shared constants re-exported for pSeoPageLoader */
export {
  SITE_URL,
  CATEGORY_LABELS,
  SECTION_LABELS,
  defaultCta,
  heroIllustrations,
  heroScreenshots,
  buildStructuredData,
  createBreadcrumbs,
};

/**
 * Arabic SEO metadata for each page.
 * Keys match the English path (without /ar/ prefix).
 */
const arabicSeoMeta = {
  "/ai-innovation-management-software": {
    seoTitle: "برنامج إدارة الابتكار بالذكاء الاصطناعي | Compelion",
    seoDescription:
      "يساعد Compelion فرق الابتكار والتحول والاستراتيجية على تقييم الأفكار وترتيب أولويات المبادرات وإنشاء دراسات الجدوى وربط عمل الابتكار بتنفيذ التسليم.",
  },
  "/idea-evaluation-software": {
    seoTitle: "برنامج تقييم الأفكار | Compelion",
    seoDescription:
      "قيّم الأفكار الجديدة باستخدام معايير تقييم منظمة ومقاييس أداء مدعومة بالذكاء الاصطناعي لاتخاذ قرارات ابتكار أفضل.",
  },
  "/innovation-portfolio-management": {
    seoTitle: "إدارة محفظة الابتكار | Compelion",
    seoDescription:
      "أدر محفظة الابتكار الخاصة بك مع رؤية كاملة للمبادرات والأولويات والتقدم والمواءمة الاستراتيجية في منصة واحدة.",
  },
  "/healthcare-innovation-management-software": {
    seoTitle: "برنامج إدارة الابتكار في الرعاية الصحية | Compelion",
    seoDescription:
      "منصة مصممة لفرق الابتكار في الرعاية الصحية لتقييم الأفكار وإدارة المشاريع وتتبع الفوائد للمرضى والمؤسسات.",
  },
  "/project-risk-and-escalation-management": {
    seoTitle: "إدارة مخاطر المشاريع والتصعيد | Compelion",
    seoDescription:
      "حدد مخاطر المشاريع وقيّمها وصعّدها بشكل استباقي مع لوحات معلومات ضمان التسليم وسير عمل الحوكمة.",
  },
  "/ai-business-case-generator": {
    seoTitle: "مولد دراسة الجدوى بالذكاء الاصطناعي | Compelion",
    seoDescription:
      "أنشئ دراسات جدوى منظمة بسرعة باستخدام الذكاء الاصطناعي مع تحليل التكلفة والعائد والمواءمة الاستراتيجية وتقييم المخاطر.",
  },
  "/project-prioritisation-software": {
    seoTitle: "برنامج ترتيب أولويات المشاريع | Compelion",
    seoDescription:
      "رتّب أولويات المشاريع والمبادرات بناءً على الأثر والمواءمة والجدوى باستخدام إطار عمل منظم للتقييم.",
  },
  "/delivery-assurance-analytics": {
    seoTitle: "تحليلات ضمان التسليم | Compelion",
    seoDescription:
      "راقب صحة التسليم وتتبع الفوائد وحدد المخاطر مبكرًا باستخدام لوحات معلومات التحليلات والتحكم الإحصائي.",
  },
  "/compare/clickup-alternative": {
    seoTitle: "بديل ClickUp لإدارة الابتكار | Compelion",
    seoDescription:
      "قارن بين Compelion و ClickUp لإدارة الابتكار وتقييم الأفكار وترتيب أولويات المشاريع وضمان التسليم.",
  },
  "/compare/asana-alternative": {
    seoTitle: "بديل Asana لإدارة الابتكار | Compelion",
    seoDescription:
      "قارن بين Compelion و Asana لإدارة الابتكار وتسليم المشاريع وتتبع الفوائد وحوكمة المحفظة.",
  },
  "/compare/motion-alternative": {
    seoTitle: "بديل Motion لإدارة الابتكار | Compelion",
    seoDescription:
      "قارن بين Compelion و Motion لإدارة الابتكار وسير عمل المشاريع وتحليلات المحفظة.",
  },
  "/compare/awork-alternative": {
    seoTitle: "بديل awork لإدارة الابتكار | Compelion",
    seoDescription:
      "قارن بين Compelion و awork لإدارة الابتكار وإدارة المشاريع وسير عمل الفريق.",
  },
  "/compare/monday-alternative": {
    seoTitle: "بديل Monday.com لإدارة الابتكار | Compelion",
    seoDescription:
      "قارن بين Compelion و Monday.com لإدارة الابتكار وترتيب أولويات المشاريع وضمان التسليم.",
  },
  "/templates/idea-evaluation-template": {
    seoTitle: "نموذج تقييم الأفكار | Compelion",
    seoDescription:
      "استخدم نموذج تقييم الأفكار المنظم لتقييم الفرص الجديدة بمعايير واضحة وإطار عمل قابل للتكرار.",
  },
  "/templates/business-case-template": {
    seoTitle: "نموذج دراسة الجدوى | Compelion",
    seoDescription:
      "نموذج دراسة جدوى جاهز للاستخدام مع أقسام للتكلفة والعائد والمخاطر والمواءمة الاستراتيجية.",
  },
  "/templates/project-prioritisation-matrix": {
    seoTitle: "مصفوفة ترتيب أولويات المشاريع | Compelion",
    seoDescription:
      "مصفوفة ترتيب أولويات لمقارنة المشاريع بناءً على الأثر والجدوى والمواءمة الاستراتيجية.",
  },
  "/templates/risk-escalation-template": {
    seoTitle: "نموذج تصعيد المخاطر | Compelion",
    seoDescription:
      "نموذج لتوثيق وتصعيد المخاطر مع معايير الخطورة وخطط التخفيف ومسارات التصعيد.",
  },
  "/templates/benefit-tracking-template": {
    seoTitle: "نموذج تتبع الفوائد | Compelion",
    seoDescription:
      "نموذج لتتبع الفوائد المتوقعة والمحققة مع مؤشرات القياس والمسؤولية وجدول المراجعة.",
  },
  "/solutions/innovation-teams": {
    seoTitle: "برنامج لفرق الابتكار | Compelion",
    seoDescription:
      "منصة مصممة لفرق الابتكار لجمع الأفكار وتقييمها وترتيب أولوياتها وتحويلها إلى مشاريع قابلة للتنفيذ.",
  },
  "/solutions/transformation-offices": {
    seoTitle: "برنامج لمكاتب التحول | Compelion",
    seoDescription:
      "أدر برامج التحول مع رؤية كاملة للمحفظة وتتبع الفوائد وحوكمة التسليم في منصة واحدة.",
  },
  "/solutions/pmo": {
    seoTitle: "برنامج لمكاتب إدارة المشاريع | Compelion",
    seoDescription:
      "منصة لمكاتب إدارة المشاريع لترتيب أولويات المبادرات ومراقبة التسليم وإدارة المخاطر والتصعيدات.",
  },
  "/solutions/strategy-teams": {
    seoTitle: "برنامج لفرق الاستراتيجية | Compelion",
    seoDescription:
      "ساعد فرق الاستراتيجية على مواءمة الابتكار مع الأهداف المؤسسية وتتبع تنفيذ المبادرات الاستراتيجية.",
  },
  "/solutions/healthcare": {
    seoTitle: "برنامج للابتكار في الرعاية الصحية | Compelion",
    seoDescription:
      "منصة لفرق الابتكار في الرعاية الصحية لتقييم الأفكار وإدارة مشاريع تحسين الجودة وتتبع الفوائد.",
  },
  "/solutions/quality-improvement": {
    seoTitle: "برنامج لتحسين الجودة | Compelion",
    seoDescription:
      "أدر مشاريع تحسين الجودة مع تقييم منظم وتتبع التسليم وقياس الأثر في منصة واحدة.",
  },
  "/learn/how-to-evaluate-business-ideas": {
    seoTitle: "كيفية تقييم أفكار الأعمال | Compelion",
    seoDescription:
      "دليل عملي لتقييم أفكار الأعمال باستخدام معايير منظمة وإطار عمل قابل للتكرار لاتخاذ قرارات ابتكار أفضل.",
  },
  "/learn/innovation-prioritisation-framework": {
    seoTitle: "إطار عمل ترتيب أولويات الابتكار | Compelion",
    seoDescription:
      "تعرف على كيفية بناء إطار عمل لترتيب أولويات الابتكار يربط بين الأثر والمواءمة الاستراتيجية والجدوى.",
  },
  "/learn/project-escalation-workflow": {
    seoTitle: "سير عمل تصعيد المشاريع | Compelion",
    seoDescription:
      "دليل لبناء سير عمل تصعيد فعال للمشاريع يضمن معالجة المخاطر والعوائق في الوقت المناسب.",
  },
  "/learn/how-to-write-a-business-case": {
    seoTitle: "كيفية كتابة دراسة جدوى | Compelion",
    seoDescription:
      "دليل خطوة بخطوة لكتابة دراسة جدوى مقنعة تشمل التكلفة والعائد والمخاطر والمواءمة الاستراتيجية.",
  },
  "/learn/healthcare-innovation-governance": {
    seoTitle: "حوكمة الابتكار في الرعاية الصحية | Compelion",
    seoDescription:
      "دليل لبناء إطار حوكمة الابتكار في الرعاية الصحية يضمن المساءلة والجودة وتتبع الفوائد للمرضى.",
  },
};

export const getArabicSeoConfigForPath = (basePath) => {
  const arMeta = arabicSeoMeta[basePath];
  const page = seoPagePathMap[basePath];
  if (!arMeta || !page) {
    return null;
  }

  return {
    title: arMeta.seoTitle,
    description: arMeta.seoDescription,
    canonicalPath: `/ar${basePath}`,
    ogType: page.type === "guide" ? "article" : "website",
    structuredData: page.structuredData,
    lang: "ar",
    alternatePaths: { en: basePath, ar: `/ar${basePath}` },
  };
};

export const getSeoConfigForPath = (pathname) => {
  const page = seoPagePathMap[pathname];
  if (!page) {
    return null;
  }

  return {
    title: page.seoTitle,
    description: page.seoDescription,
    canonicalPath: page.path,
    ogType: page.type === "guide" ? "article" : "website",
    structuredData: page.structuredData,
    alternatePaths: arabicSeoMeta[page.path]
      ? { en: page.path, ar: `/ar${page.path}` }
      : null,
  };
};
